import { Suspense, useEffect, useState } from "react";

import { BookErrorBoundary } from "./book/BookErrorBoundary";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";

// Use the reload-on-stale-chunk wrapper everywhere we'd reach for
// React.lazy, so a deploy landing while a tab is open recovers itself
// instead of throwing "Failed to fetch dynamically imported module".
import { lazyWithReload as lazy } from "./lib/lazy-with-reload";

import { NavHistoryProvider } from "./components/nav-history";
import { PageLayout } from "./components/PageLayout";
import { UserAvatarProvider } from "./components/UserAvatarDirectory";
import { RequireRole } from "./components/RequireRole";
import { ToastProvider } from "./components/Toast";
import { UndoToast } from "./components/UndoToast";
import { HubTableCards } from "./components/marketing/HubTableCards";
import { CleanupProvider } from "./cleanup/CleanupProvider";
import { EmailComposerProvider } from "./contexts/EmailComposerContext";
const AcceptInvitePage = lazy(() =>
  import("./pages/AcceptInvitePage").then((m) => ({ default: m.AcceptInvitePage })),
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ClientUploadPage = lazy(() =>
  import("./pages/ClientUploadPage").then((m) => ({ default: m.ClientUploadPage })),
);
const CallRecordingUploadPage = lazy(() =>
  import("./pages/CallRecordingUploadPage").then((m) => ({
    default: m.CallRecordingUploadPage,
  })),
);
const IntakeFormPage = lazy(() =>
  import("./pages/IntakeFormPage").then((m) => ({ default: m.IntakeFormPage })),
);
const FormPublicPage = lazy(() =>
  import("./pages/FormPublicPage").then((m) => ({ default: m.FormPublicPage })),
);
const EsignSignPage = lazy(() =>
  import("./pages/EsignSignPage").then((m) => ({ default: m.EsignSignPage })),
);
// Marketing Hub: the one question's answer page (public, the link is the key).
const AnswerQuestionPage = lazy(() =>
  import("./pages/AnswerQuestionPage").then((m) => ({ default: m.AnswerQuestionPage })),
);
const PreferenceCenterPage = lazy(() =>
  import("./pages/PreferenceCenterPage").then((m) => ({ default: m.PreferenceCenterPage })),
);
const CheckInPage = lazy(() =>
  import("./pages/CheckInPage").then((m) => ({ default: m.CheckInPage })),
);
const DigestPage = lazy(() =>
  import("./pages/DigestPage").then((m) => ({ default: m.DigestPage })),
);
// Marketing Hub row 4.4: the review ask's private page (public, the link is the key).
const ReviewAskPage = lazy(() =>
  import("./pages/ReviewAskPage").then((m) => ({ default: m.ReviewAskPage })),
);
// Marketing Hub row 4.5: the introduction ask's page (public, the link is the key).
const IntroducePage = lazy(() =>
  import("./pages/IntroducePage").then((m) => ({ default: m.IntroducePage })),
);
const EsignCompleteBridgePage = lazy(() =>
  import("./pages/EsignCompleteBridgePage").then((m) => ({
    default: m.EsignCompleteBridgePage,
  })),
);
const BookFrontDoorPage = lazy(() =>
  import("./pages/book/BookFrontDoorPage").then((m) => ({ default: m.BookFrontDoorPage })),
);
const BookWithPage = lazy(() =>
  import("./pages/book/BookFrontDoorPage").then((m) => ({ default: m.BookWithPage })),
);
const BookInvitePage = lazy(() =>
  import("./pages/book/BookInvitePage").then((m) => ({ default: m.BookInvitePage })),
);
const BookManagePage = lazy(() =>
  import("./pages/book/BookManagePage").then((m) => ({ default: m.BookManagePage })),
);
const FormsPage = lazy(() => import("./pages/FormsPage").then((m) => ({ default: m.FormsPage })));
const ChecklistsPage = lazy(() =>
  import("./pages/ChecklistsPage").then((m) => ({ default: m.ChecklistsPage })),
);
const RequestLinkPage = lazy(() =>
  import("./pages/RequestLinkPage").then((m) => ({ default: m.RequestLinkPage })),
);
const RequestPanelPage = lazy(() =>
  import("./pages/RequestPanelPage").then((m) => ({ default: m.RequestPanelPage })),
);
const ChecklistEditorPage = lazy(() =>
  import("./pages/ChecklistsPage").then((m) => ({ default: m.ChecklistEditorPage })),
);
const FormBuilderPage = lazy(() =>
  import("./pages/FormBuilderPage").then((m) => ({ default: m.FormBuilderPage })),
);
const FormSubmissionsPage = lazy(() =>
  import("./pages/FormSubmissionsPage").then((m) => ({ default: m.FormSubmissionsPage })),
);
const FormResultsPage = lazy(() =>
  import("./pages/FormResultsPage").then((m) => ({ default: m.FormResultsPage })),
);
const AdminCommissionSplitsPage = lazy(() =>
  import("./pages/AdminCommissionSplitsPage").then((m) => ({
    default: m.AdminCommissionSplitsPage,
  })),
);
const AdminDataImportFinancialPage = lazy(() =>
  import("./pages/AdminDataImportFinancialPage").then((m) => ({
    default: m.AdminDataImportFinancialPage,
  })),
);
const AdminCommissionReconciliationPage = lazy(() =>
  import("./pages/AdminCommissionReconciliationPage").then((m) => ({
    default: m.AdminCommissionReconciliationPage,
  })),
);
const AdminInsuranceReconciliationPage = lazy(() =>
  import("./pages/AdminInsuranceReconciliationPage").then((m) => ({
    default: m.AdminInsuranceReconciliationPage,
  })),
);
const InsuranceDashboardPage = lazy(() =>
  import("./pages/InsuranceDashboardPage").then((m) => ({ default: m.InsuranceDashboardPage })),
);
const PlanningDepositsPage = lazy(() =>
  import("./pages/PlanningDepositsPage").then((m) => ({
    default: m.PlanningDepositsPage,
  })),
);
const PlanningBookOfBusinessPage = lazy(() =>
  import("./pages/PlanningBookOfBusinessPage").then((m) => ({
    default: m.PlanningBookOfBusinessPage,
  })),
);
const StructuredNotesPage = lazy(() =>
  import("./pages/StructuredNotesPage").then((m) => ({
    default: m.StructuredNotesPage,
  })),
);
const AdminFinancialFirmDashboardPage = lazy(() =>
  import("./pages/AdminFinancialFirmDashboardPage").then((m) => ({
    default: m.AdminFinancialFirmDashboardPage,
  })),
);
const ReferralNetworkPage = lazy(() =>
  import("./pages/ReferralNetworkPage").then((m) => ({ default: m.ReferralNetworkPage })),
);
const AdminFeeReviewPage = lazy(() =>
  import("./pages/AdminFeeReviewPage").then((m) => ({
    default: m.AdminFeeReviewPage,
  })),
);
const AdminFinancialDepositsPage = lazy(() =>
  import("./pages/AdminFinancialDepositsPage").then((m) => ({
    default: m.AdminFinancialDepositsPage,
  })),
);
const MyMortgageCompliancePage = lazy(() =>
  import("./pages/MyMortgageCompliancePage").then((m) => ({ default: m.MyMortgageCompliancePage })),
);
const LoanLogPage = lazy(() =>
  import("./pages/LoanLogPage").then((m) => ({ default: m.LoanLogPage })),
);
// Row 10.5: credit inquiries on our own borrowers, Mortgage only.
const MortgageWatchPage = lazy(() =>
  import("./pages/MortgageWatchPage").then((m) => ({ default: m.MortgageWatchPage })),
);
const LoanFilePage = lazy(() => import("./pages/LoanFilePage").then((m) => ({ default: m.LoanFilePage })));
const ClientSignInPage = lazy(() =>
  import("./pages/ClientSignInPage").then((m) => ({ default: m.ClientSignInPage })),
);
const MortgageCompliancePage = lazy(() =>
  import("./pages/MortgageCompliancePage").then((m) => ({
    default: m.MortgageCompliancePage,
  })),
);
const ClosedLoanReportPage = lazy(() =>
  import("./pages/ClosedLoanReportPage").then((m) => ({ default: m.ClosedLoanReportPage })),
);
const CallReportPage = lazy(() =>
  import("./pages/CallReportPage").then((m) => ({ default: m.CallReportPage })),
);
const AdminCompliancePage = lazy(() =>
  import("./pages/AdminCompliancePage").then((m) => ({ default: m.AdminCompliancePage })),
);
const AdminCompanyCompliancePage = lazy(() =>
  import("./pages/AdminCompanyCompliancePage").then((m) => ({
    default: m.AdminCompanyCompliancePage,
  })),
);
// D2 cutover (2026-05-18): the Data Hygiene dashboard + dedup /
// structure / data-quality queue pages were deleted (replaced by
// /cleanup). E2 cutover (2026-05-19): AdminPendingConflictsPage is
// now also deleted — the source-priority-conflict detector makes
// /cleanup a superset of it; the old route 302s to /cleanup.
const DataNeededPage = lazy(() =>
  import("./pages/DataNeededPage").then((m) => ({ default: m.DataNeededPage })),
);
const UpcomingCommunicationsPage = lazy(() =>
  import("./pages/UpcomingCommunicationsPage").then((m) => ({
    default: m.UpcomingCommunicationsPage,
  })),
);
const AutomationsOnMyClientsPage = lazy(() =>
  import("./pages/AutomationsOnMyClientsPage").then((m) => ({
    default: m.AutomationsOnMyClientsPage,
  })),
);
const JudgmentTeamPage = lazy(() =>
  import("./pages/JudgmentTeamPage").then((m) => ({ default: m.JudgmentTeamPage })),
);
const SecurityPage = lazy(() =>
  import("./pages/SecurityPage").then((m) => ({ default: m.SecurityPage })),
);
// /cleanup → /data-needed, keeping ?issue= (the one deep-link the old
// workspace handed out). Everything else on the old URL (lanes, company)
// is a filter the new page derives from the viewer's scope.
// /transactions/:id → /processing/:id. The household cards linked to the old
// path for a while (Nate, 2026-10-13: the link dropped him on his dashboard),
// so old bookmarks and open tabs keep working.
function TransactionToProcessing() {
  const { id } = useParams();
  return <Navigate to={`/processing/${encodeURIComponent(id ?? "")}`} replace />;
}

function CleanupToDataNeeded() {
  const [params] = useSearchParams();
  const issue = params.get("issue");
  return (
    <Navigate
      to={issue ? `/data-needed?issue=${encodeURIComponent(issue)}` : "/data-needed"}
      replace
    />
  );
}

const CleanupHelpPage = lazy(() =>
  import("./pages/CleanupHelpPage").then((m) => ({ default: m.CleanupHelpPage })),
);
const AdminTeamTabbedPage = lazy(() =>
  import("./pages/AdminTeamTabbedPage").then((m) => ({ default: m.AdminTeamTabbedPage })),
);
// Pay runs (commissions build program B3): payroll by pay date, one
// company's run, and the unpaid earnings ledger. Signed-in employees only;
// the server decides who sees which company (Grant, system admins, and each
// company's backup approver), so the backups who are not admins can open them.
const PayDatePage = lazy(() =>
  import("./pages/pay-runs/PayDatePage").then((m) => ({ default: m.PayDatePage })),
);
const PayRunsIndex = lazy(() =>
  import("./pages/pay-runs/PayDatePage").then((m) => ({ default: m.PayRunsIndex })),
);
const PayRunPage = lazy(() =>
  import("./pages/pay-runs/PayRunPage").then((m) => ({ default: m.PayRunPage })),
);
const JohnHancockFilePage = lazy(() =>
  import("./pages/pay-runs/JohnHancockFilePage").then((m) => ({ default: m.JohnHancockFilePage })),
);
const DeskPage = lazy(() =>
  import("./pages/ops-desk/DeskPage").then((m) => ({ default: m.DeskPage })),
);
const StatementPage = lazy(() =>
  import("./pages/pay-runs/StatementPage").then((m) => ({ default: m.StatementPage })),
);
const UnpaidEarningsPage = lazy(() =>
  import("./pages/pay-runs/UnpaidEarningsPage").then((m) => ({ default: m.UnpaidEarningsPage })),
);
const CompPlanPage = lazy(() =>
  import("./pages/comp-plans/CompPlanPage").then((m) => ({ default: m.CompPlanPage })),
);
const MyCompPlanPage = lazy(() =>
  import("./pages/comp-plans/CompPlanPage").then((m) => ({ default: m.MyCompPlanPage })),
);
const TeamCompensationPage = lazy(() =>
  import("./pages/comp-plans/TeamCompensationPage").then((m) => ({ default: m.TeamCompensationPage })),
);
const AdminExamPacksPage = lazy(() => import("./pages/AdminExamPacksPage"));
const AdminLegalMattersPage = lazy(() => import("./pages/AdminLegalMattersPage"));
const CompanyCoveragePage = lazy(() => import("./pages/CompanyCoveragePage"));
const CompanyPolicyPage = lazy(() => import("./pages/CompanyPolicyPage"));
const CompanyPolicyFormPage = lazy(() => import("./pages/CompanyPolicyFormPage"));
const CompanyCoverageImportPage = lazy(() => import("./pages/CompanyCoverageImportPage"));
const CompanyRenewalReviewPage = lazy(() => import("./pages/CompanyRenewalReviewPage"));
const CompanyCoverageCheckPage = lazy(() => import("./pages/CompanyCoverageCheckPage"));
const CompanyCoverageMissingPage = lazy(() => import("./pages/CompanyCoverageMissingPage"));
const AdminRetentionSchedulePage = lazy(() => import("./pages/AdminRetentionSchedulePage"));
const AdminStewFeedbackPage = lazy(() =>
  import("./pages/AdminStewFeedbackPage").then((m) => ({ default: m.AdminStewFeedbackPage })),
);
const AdminDatabaseHealthPage = lazy(() =>
  import("./pages/AdminDatabaseHealthPage").then((m) => ({
    default: m.AdminDatabaseHealthPage,
  })),
);
const AdminPlaidLiveTestPage = lazy(() =>
  import("./pages/AdminPlaidLiveTestPage").then((m) => ({
    default: m.AdminPlaidLiveTestPage,
  })),
);
const PlaidBankReturnPage = lazy(() =>
  import("./pages/PlaidBankReturnPage").then((m) => ({
    default: m.PlaidBankReturnPage,
  })),
);
const AdminEmailHealthPage = lazy(() =>
  import("./pages/AdminEmailHealthPage").then((m) => ({
    default: m.AdminEmailHealthPage,
  })),
);
const AdminStagingOutboxPage = lazy(() =>
  import("./pages/AdminStagingOutboxPage").then((m) => ({
    default: m.AdminStagingOutboxPage,
  })),
);
const AdminBookingRoutingPage = lazy(() =>
  import("./pages/AdminBookingRoutingPage").then((m) => ({
    default: m.AdminBookingRoutingPage,
  })),
);
const AdminIntegrationsPage = lazy(() =>
  import("./pages/AdminIntegrationsPage").then((m) => ({ default: m.AdminIntegrationsPage })),
);
const AdminIntegrationsMicrosoftGraphPage = lazy(() =>
  import("./pages/AdminIntegrationsMicrosoftGraphPage").then((m) => ({
    default: m.AdminIntegrationsMicrosoftGraphPage,
  })),
);
const AdminSharePointMigratePage = lazy(() =>
  import("./pages/AdminSharePointMigratePage").then((m) => ({
    default: m.AdminSharePointMigratePage,
  })),
);
const AdminFitCleanupPage = lazy(() =>
  import("./pages/AdminFitCleanupPage").then((m) => ({ default: m.AdminFitCleanupPage })),
);
const AdminIntegrationsGustoPage = lazy(() =>
  import("./pages/AdminIntegrationsGustoPage").then((m) => ({
    default: m.AdminIntegrationsGustoPage,
  })),
);
const AdminIntegrationsQuickBooksPage = lazy(() =>
  import("./pages/AdminIntegrationsQuickBooksPage").then((m) => ({
    default: m.AdminIntegrationsQuickBooksPage,
  })),
);
const AdminIntegrationsAdAudiencesPage = lazy(() =>
  import("./pages/AdminIntegrationsAdAudiencesPage").then((m) => ({
    default: m.AdminIntegrationsAdAudiencesPage,
  })),
);
const AdminIntegrationsIvansPage = lazy(() =>
  import("./pages/AdminIntegrationsIvansPage").then((m) => ({
    default: m.AdminIntegrationsIvansPage,
  })),
);
const CarrierActivityPage = lazy(() =>
  import("./pages/CarrierActivityPage").then((m) => ({
    default: m.CarrierActivityPage,
  })),
);
const IvansMatchReviewPage = lazy(() =>
  import("./pages/IvansMatchReviewPage").then((m) => ({
    default: m.IvansMatchReviewPage,
  })),
);
const AdminIntegrationsBookingsPage = lazy(() =>
  import("./pages/AdminIntegrationsBookingsPage").then((m) => ({
    default: m.AdminIntegrationsBookingsPage,
  })),
);
const AdminDashboardsTargetsPage = lazy(() =>
  import("./pages/AdminDashboardsTargetsPage").then((m) => ({
    default: m.AdminDashboardsTargetsPage,
  })),
);
const AdminSettingsPage = lazy(() =>
  import("./pages/AdminSettingsPage").then((m) => ({ default: m.AdminSettingsPage })),
);
const AtRiskCalibrationPage = lazy(() =>
  import("./pages/AtRiskCalibrationPage").then((m) => ({ default: m.AtRiskCalibrationPage })),
);
// Watchlist Rules (2026-09-22): every number the at-risk engine scores
// with, editable with a history. Admin → Settings; no sidebar item.
const WatchlistRulesPage = lazy(() =>
  import("./pages/WatchlistRulesPage").then((m) => ({ default: m.WatchlistRulesPage })),
);
const AdminViewDefaultsPage = lazy(() =>
  import("./pages/AdminViewDefaultsPage").then((m) => ({ default: m.AdminViewDefaultsPage })),
);
const AdminFirmProfilesPage = lazy(() =>
  import("./pages/AdminFirmProfilesPage").then((m) => ({ default: m.AdminFirmProfilesPage })),
);
const PipelineBoardsAdminPage = lazy(() =>
  import("./pages/PipelineBoardsAdminPage").then((m) => ({ default: m.PipelineBoardsAdminPage })),
);
const AdminSharedMailboxAccessPage = lazy(() =>
  import("./pages/AdminSharedMailboxAccessPage").then((m) => ({
    default: m.AdminSharedMailboxAccessPage,
  })),
);
const AutomationBuilderPage = lazy(() =>
  import("./pages/AutomationBuilderPage").then((m) => ({ default: m.AutomationBuilderPage })),
);
const AutomationsPage = lazy(() =>
  import("./pages/AutomationsPage").then((m) => ({ default: m.AutomationsPage })),
);
const SystemJobsPage = lazy(() =>
  import("./pages/SystemJobsPage").then((m) => ({ default: m.SystemJobsPage })),
);
import {
  CommissionPayrollPage,
  CommissionsListPage,
  CommissionWorksheetPage,
} from "./pages/CommissionPages";
const SavedEmailPage = lazy(() =>
  import("./pages/SavedEmailPage").then((m) => ({ default: m.SavedEmailPage })),
);
const EmailPage = lazy(() => import("./pages/EmailPage").then((m) => ({ default: m.EmailPage })));
const TasksPage = lazy(() => import("./pages/TasksPage").then((m) => ({ default: m.TasksPage })));
const CalendarPage = lazy(() =>
  import("./pages/CalendarPage").then((m) => ({ default: m.CalendarPage })),
);
const AppointmentsPage = lazy(() =>
  import("./pages/AppointmentsPage").then((m) => ({ default: m.AppointmentsPage })),
);
const ClientSnapshotPage = lazy(() =>
  import("./pages/ClientSnapshotPage").then((m) => ({ default: m.ClientSnapshotPage })),
);
const PlanningRequoteWorksheetPage = lazy(() =>
  import("./pages/PlanningRequoteWorksheetPage").then((m) => ({
    default: m.PlanningRequoteWorksheetPage,
  })),
);
const AcaQuotePage = lazy(() =>
  import("./pages/AcaQuotePage").then((m) => ({ default: m.AcaQuotePage })),
);
const MedicareQuotePage = lazy(() =>
  import("./pages/MedicareQuotePage").then((m) => ({ default: m.MedicareQuotePage })),
);
const CompanyDashboardPage = lazy(() =>
  import("./pages/CompanyDashboardPage").then((m) => ({ default: m.CompanyDashboardPage })),
);
const ContactPage = lazy(() =>
  import("./pages/ContactPage").then((m) => ({ default: m.ContactPage })),
);
const ContactsPage = lazy(() =>
  import("./pages/ContactsListPage").then((m) => ({ default: m.ContactsListPage })),
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const HouseholdCompanyPage = lazy(() =>
  import("./pages/HouseholdCompanyPage").then((m) => ({ default: m.HouseholdCompanyPage })),
);
const HouseholdPage = lazy(() =>
  import("./pages/HouseholdPage").then((m) => ({ default: m.HouseholdPage })),
);
// A trust's own page (2026-10-14; docs/design/trusts.md).
const TrustPage = lazy(() => import("./pages/TrustPage").then((m) => ({ default: m.TrustPage })));
// PR 6: OngoingCarePage + OngoingCareCalendarPage retired — replaced by
// OngoingCareHome (calendar-first command center). The old files remain
// unreferenced pending a Phase-G cleanup pass.
const OngoingCareHome = lazy(() =>
  import("./pages/OngoingCareHome").then((m) => ({ default: m.OngoingCareHome })),
);
const OpportunityAgingPage = lazy(() =>
  import("./pages/OpportunityAgingPage").then((m) => ({ default: m.OpportunityAgingPage })),
);
const OpportunityBoardPage = lazy(() =>
  import("./pages/OpportunityBoardPage").then((m) => ({ default: m.OpportunityBoardPage })),
);
const OpportunityForecastPage = lazy(() =>
  import("./pages/OpportunityForecastPage").then((m) => ({ default: m.OpportunityForecastPage })),
);
const ServiceRecordDetailPage = lazy(() =>
  import("./pages/ServiceRecordDetailPage").then((m) => ({ default: m.ServiceRecordDetailPage })),
);
const VendorsPage = lazy(() =>
  import("./pages/VendorsPage").then((m) => ({ default: m.VendorsPage })),
);
const HelpPage = lazy(() => import("./pages/HelpPage").then((m) => ({ default: m.HelpPage })));
const EmployersPage = lazy(() =>
  import("./pages/EmployersPage").then((m) => ({ default: m.EmployersPage })),
);
const EmployerDetailPage = lazy(() =>
  import("./pages/EmployerDetailPage").then((m) => ({ default: m.EmployerDetailPage })),
);
const GroupPlanPage = lazy(() =>
  import("./pages/GroupPlanPage").then((m) => ({ default: m.GroupPlanPage })),
);
const GroupPlanBringInPage = lazy(() =>
  import("./pages/GroupPlanBringInPage").then((m) => ({ default: m.GroupPlanBringInPage })),
);
const VendorDetailPage = lazy(() =>
  import("./pages/VendorDetailPage").then((m) => ({ default: m.VendorDetailPage })),
);
const ServiceRecordsListPage = lazy(() =>
  import("./pages/ServiceRecordsListPage").then((m) => ({ default: m.ServiceRecordsListPage })),
);
const HouseholdsPage = lazy(() =>
  import("./pages/HouseholdsPage").then((m) => ({ default: m.HouseholdsPage })),
);
const LoanFileAuditPage = lazy(() =>
  import("./pages/LoanFileAuditPage").then((m) => ({ default: m.LoanFileAuditPage })),
);
const LicenseLookupPage = lazy(() =>
  import("./pages/LicenseLookupPage").then((m) => ({ default: m.LicenseLookupPage })),
);
// Every page the person may open, grouped like the menus (navigation
// overhaul, 2026-10-15). Reached from the "All pages" button at the foot
// of the left rail.
const AllPagesPage = lazy(() =>
  import("./pages/AllPagesPage").then((m) => ({ default: m.AllPagesPage })),
);
const PmGuidelinesPage = lazy(() =>
  import("./pages/PmGuidelinesPage").then((m) => ({ default: m.PmGuidelinesPage })),
);
const PmUnderwritingPage = lazy(() =>
  import("./pages/PmUnderwritingPage").then((m) => ({ default: m.PmUnderwritingPage })),
);
const PmFundPage = lazy(() =>
  import("./pages/PmFundPage").then((m) => ({ default: m.PmFundPage })),
);
const IndustryDocumentsPage = lazy(() =>
  import("./pages/IndustryDocumentsPage").then((m) => ({ default: m.IndustryDocumentsPage })),
);
const EsignTemplatesPage = lazy(() =>
  import("./pages/EsignTemplatesPage").then((m) => ({ default: m.EsignTemplatesPage })),
);
const EsignLearnedCaptionsPage = lazy(() =>
  import("./pages/EsignLearnedCaptionsPage").then((m) => ({
    default: m.EsignLearnedCaptionsPage,
  })),
);
import { MyCommissionsPage } from "./components/MyCommissionsWidget";
const NotificationsPage = lazy(() =>
  import("./pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })),
);
const DonnaDashboardPage = lazy(() =>
  import("./pages/DonnaDashboardPage").then((m) => ({ default: m.DonnaDashboardPage })),
);
const AdminAmbassadorFeesPage = lazy(() =>
  import("./pages/AdminAmbassadorFeesPage").then((m) => ({
    default: m.AdminAmbassadorFeesPage,
  })),
);
const AmbassadorAvailabilityPage = lazy(() =>
  import("./pages/AmbassadorAvailabilityPage").then((m) => ({
    default: m.AmbassadorAvailabilityPage,
  })),
);
const AmbassadorReferralsPage = lazy(() =>
  import("./pages/AmbassadorReferralsPage").then((m) => ({ default: m.AmbassadorReferralsPage })),
);
const AdvisorSchedulePage = lazy(() =>
  import("./pages/AdvisorSchedulePage").then((m) => ({ default: m.AdvisorSchedulePage })),
);
const BriefingDashboard = lazy(() =>
  import("./pages/BriefingDashboard").then((m) => ({ default: m.BriefingDashboard })),
);
const CockpitDashboard = lazy(() =>
  import("./pages/CockpitDashboard").then((m) => ({ default: m.CockpitDashboard })),
);
const RhythmDetailPage = lazy(() =>
  import("./pages/RhythmDetailPage").then((m) => ({ default: m.RhythmDetailPage })),
);
// Ongoing Care v2, step 4 (2026-10-03): the single-household wizard is
// retired. Its address now opens the family's Ongoing care tab, where
// every plan they could be on has an Add button, or the Plans list when
// no family is named. The page files stay until Grant confirms removing
// them.
function RetiredNewRhythmRedirect() {
  const [params] = useSearchParams();
  const household = params.get("household");
  return (
    <Navigate
      replace
      to={
        household
          ? `/households/${encodeURIComponent(household)}?tab=rhythms`
          : "/ongoing-care/plans"
      }
    />
  );
}
const CarePlanDefinitionsPage = lazy(() => import("./pages/CarePlanDefinitionsPage"));
const NewCarePlanDefinitionPage = lazy(() => import("./pages/NewCarePlanDefinitionPage"));
const CarePlanDefinitionPage = lazy(() => import("./pages/CarePlanDefinitionPage"));
const EditRhythmPage = lazy(() =>
  import("./pages/EditRhythmPage").then((m) => ({ default: m.EditRhythmPage })),
);
const MonthDrilldownPage = lazy(() =>
  import("./pages/MonthDrilldownPage").then((m) => ({ default: m.MonthDrilldownPage })),
);
const OpportunitiesPage = lazy(() =>
  import("./pages/OpportunitiesPage").then((m) => ({ default: m.OpportunitiesPage })),
);
const OpportunityDetailPage = lazy(() =>
  import("./pages/OpportunityDetailPage").then((m) => ({ default: m.OpportunityDetailPage })),
);
const ProcessingPage = lazy(() =>
  import("./pages/ProcessingPage").then((m) => ({ default: m.ProcessingPage })),
);
const ProcessingBoardPage = lazy(() =>
  import("./pages/ProcessingBoardPage").then((m) => ({ default: m.ProcessingBoardPage })),
);
// The E-Sign workspace (Grant, 2026-08-23). The nav session added the
// left-rail row pointing here; this is the route it points at.
const EsignPage = lazy(() => import("./pages/EsignPage").then((m) => ({ default: m.EsignPage })));
const EsignEnvelopePage = lazy(() =>
  import("./pages/EsignEnvelopePage").then((m) => ({ default: m.EsignEnvelopePage })),
);
const EsignSettingsPage = lazy(() =>
  import("./pages/EsignSettingsPage").then((m) => ({ default: m.EsignSettingsPage })),
);
const TransactionDetailPage = lazy(() =>
  import("./pages/TransactionDetailPage").then((m) => ({ default: m.TransactionDetailPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const PulsePage = lazy(() => import("./pages/PulsePage").then((m) => ({ default: m.PulsePage })));
const ReportsPage = lazy(() =>
  import("./pages/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);
const SavedViewPage = lazy(() =>
  import("./pages/SavedViewPage").then((m) => ({ default: m.SavedViewPage })),
);
const SignInPage = lazy(() =>
  import("./pages/SignInPage").then((m) => ({ default: m.SignInPage })),
);
const CommunicationTemplatesPage = lazy(() =>
  import("./pages/CommunicationTemplatesPage").then((m) => ({
    default: m.CommunicationTemplatesPage,
  })),
);
const CommunicationsPage = lazy(() =>
  import("./pages/CommunicationsPage").then((m) => ({ default: m.CommunicationsPage })),
);
const MyDayPage = lazy(() => import("./pages/MyDayPage").then((m) => ({ default: m.MyDayPage })));
const TrendsPage = lazy(() =>
  import("./pages/TrendsPage").then((m) => ({ default: m.TrendsPage })),
);
const WatchlistPage = lazy(() =>
  import("./pages/WatchlistPage").then((m) => ({ default: m.WatchlistPage })),
);
const ServicesCheckListPage = lazy(() =>
  import("./pages/ServicesCheckListPage").then((m) => ({
    default: m.ServicesCheckListPage,
  })),
);
const FounderStudioPage = lazy(() =>
  import("./pages/FounderStudioPage").then((m) => ({
    default: m.FounderStudioPage,
  })),
);
const MarketingCampaignsPage = lazy(() =>
  import("./pages/MarketingCampaignsPage").then((m) => ({
    default: m.MarketingCampaignsPage,
  })),
);
const MarketingCampaignPage = lazy(() =>
  import("./pages/MarketingCampaignPage").then((m) => ({
    default: m.MarketingCampaignPage,
  })),
);
const MarketingGoalPage = lazy(() =>
  import("./pages/MarketingGoalPage").then((m) => ({
    default: m.MarketingGoalPage,
  })),
);
const MarketingApprovalsPage = lazy(() =>
  import("./pages/MarketingApprovalsPage").then((m) => ({
    default: m.MarketingApprovalsPage,
  })),
);
const MarketingLevelsPage = lazy(() =>
  import("./pages/MarketingLevelsPage").then((m) => ({
    default: m.MarketingLevelsPage,
  })),
);
const MarketingPlaybooksPage = lazy(() =>
  import("./pages/MarketingPlaybooksPage").then((m) => ({
    default: m.MarketingPlaybooksPage,
  })),
);
const MarketingPlaysPage = lazy(() =>
  import("./pages/MarketingPlaysPage").then((m) => ({
    default: m.MarketingPlaysPage,
  })),
);
const MarketingSeriesPage = lazy(() =>
  import("./pages/MarketingSeriesPage").then((m) => ({
    default: m.MarketingSeriesPage,
  })),
);
const MarketingEmailPage = lazy(() =>
  import("./pages/MarketingEmailPage").then((m) => ({
    default: m.MarketingEmailPage,
  })),
);
const MarketingBlogPage = lazy(() =>
  import("./pages/MarketingBlogPage").then((m) => ({
    default: m.MarketingBlogPage,
  })),
);
const MarketingSocialPage = lazy(() =>
  import("./pages/MarketingSocialPage").then((m) => ({
    default: m.MarketingSocialPage,
  })),
);
const MarketingCalendarPage = lazy(() =>
  import("./pages/MarketingCalendarPage").then((m) => ({
    default: m.MarketingCalendarPage,
  })),
);
const MarketingContentPage = lazy(() =>
  import("./pages/MarketingContentPage").then((m) => ({
    default: m.MarketingContentPage,
  })),
);
const MarketingPostsPage = lazy(() =>
  import("./pages/MarketingPostsPage").then((m) => ({
    default: m.MarketingPostsPage,
  })),
);
// Row 8.2: /marketing/pieces/:id is the spec's route for every kind of
// piece; ?kind=post opens the social composer, ?kind=blog the blog builder,
// ?kind=ad the ad builder (row 9b.2), ?kind=search the search campaign
// builder (row 9b.3), anything else the email builder
// (whose links never carry a kind).
function MarketingPiecePage() {
  const [params] = useSearchParams();
  const kind = params.get("kind");
  return kind === "post" ? <MarketingSocialPage /> : kind === "blog" ? <MarketingBlogPage /> : kind === "ad" ? <MarketingAdPage /> : kind === "search" ? <MarketingSearchPage /> : <MarketingEmailPage />;
}
// Row 9b.2: ?kind=ad opens the Meta ad builder (spec screen 17).
const MarketingAdPage = lazy(() =>
  import("./pages/MarketingAdPage").then((m) => ({
    default: m.MarketingAdPage,
  })),
);
// Row 9b.3: ?kind=search opens the Google search campaign builder (spec screen 18).
const MarketingSearchPage = lazy(() =>
  import("./pages/MarketingSearchPage").then((m) => ({
    default: m.MarketingSearchPage,
  })),
);
const MarketingSettingsPage = lazy(() =>
  import("./pages/MarketingSettingsPage").then((m) => ({
    default: m.MarketingSettingsPage,
  })),
);
const MarketingOpportunitiesPage = lazy(() =>
  import("./pages/MarketingOpportunitiesPage").then((m) => ({
    default: m.MarketingOpportunitiesPage,
  })),
);
const MarketingReferralsPage = lazy(() =>
  import("./pages/MarketingReferralsPage").then((m) => ({
    default: m.MarketingReferralsPage,
  })),
);
const MarketingInboxPage = lazy(() =>
  import("./pages/MarketingInboxPage").then((m) => ({
    default: m.MarketingInboxPage,
  })),
);
const MarketingEventsPage = lazy(() =>
  import("./pages/MarketingEventsPage").then((m) => ({
    default: m.MarketingEventsPage,
  })),
);
const MarketingMailPage = lazy(() =>
  import("./pages/MarketingMailPage").then((m) => ({
    default: m.MarketingMailPage,
  })),
);
const MarketingEmailClientsPage = lazy(() =>
  import("./pages/MarketingEmailClientsPage").then((m) => ({
    default: m.MarketingEmailClientsPage,
  })),
);
const MarketingEventPage = lazy(() =>
  import("./pages/MarketingEventPage").then((m) => ({
    default: m.MarketingEventPage,
  })),
);
const MarketingReviewsPage = lazy(() =>
  import("./pages/MarketingReviewsPage").then((m) => ({
    default: m.MarketingReviewsPage,
  })),
);
const MarketingSharePage = lazy(() =>
  import("./pages/MarketingSharePage").then((m) => ({
    default: m.MarketingSharePage,
  })),
);
const MarketingResultsPage = lazy(() =>
  import("./pages/MarketingResultsPage").then((m) => ({
    default: m.MarketingResultsPage,
  })),
);
const MarketingLeaderboardPage = lazy(() =>
  import("./pages/MarketingLeaderboardPage").then((m) => ({
    default: m.MarketingLeaderboardPage,
  })),
);
const MarketingTodayPage = lazy(() =>
  import("./pages/MarketingTodayPage").then((m) => ({
    default: m.MarketingTodayPage,
  })),
);
const WorkloadPlannerPage = lazy(() =>
  import("./pages/WorkloadPlannerPage").then((m) => ({ default: m.WorkloadPlannerPage })),
);
import { SessionProvider, useSession } from "./session";
import { UserSettingsProvider } from "./settings/UserSettingsProvider";
import { MfaProvider, useMfa } from "./MfaContext";
import { isPublicFormsHost } from "./forms/forms-host";
import { AppBootSkeleton, LoadingBlock } from "./ui";
import {
  canManageSharedMailboxAccess,
  canRunFitCleanup,
  canRunSharePointMigration,
  canSeeAdminSection,
  canSeeCommissionReconciliation,
  canOpenSharedCompanyPolicy,
  canSeeCompanyCoverage,
  canSeeAutomations,
  canSeeAutomationsNav,
  canSeePlanningBook,
  canSeePlanningDeposits,
  canSeeFeeReview,
  canSeeStructuredNotes,
  canSeeTeamPage,
} from "./utils/role-visibility";
import { roleHomePath } from "./utils/role-home";

// Guard for every authenticated route. Two checks:
//
//   1. Authentication — signed-out users bounce to /sign-in with
//      their destination preserved.
//   2. Employee mapping — a cookie that verifies but points at a
//      users row without an active employee_role_slug means the
//      account is orphaned (the credential exists but the user
//      isn't configured to do anything). We sign it out and show a
//      "not fully configured" toast rather than letting it reach
//      protected pages. The only v1 path to this state is a manual
//      DB edit or a migration bug; surfacing it loudly is
//      preferable to silently rendering an empty app shell.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status, user, signOut, sessionExpired } = useSession();
  const [unmappedNotice, setUnmappedNotice] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (
      status === "signed_in" &&
      user &&
      (user.user_type !== "employee" || !user.employee_role_slug)
    ) {
      // Set the notice BEFORE calling signOut — signOut() transitions
      // status to signed_out, which unmounts this guard's children
      // and re-renders SignInPage. We pass the notice forward via
      // the `from` state so SignInPage can surface it.
      setUnmappedNotice("Account not fully configured. Contact your administrator.");
      void signOut();
    }
  }, [status, user, signOut]);

  if (status === "loading") {
    return <AppBootSkeleton />;
  }
  if (status === "signed_out") {
    // Notice precedence: an orphaned-account sign-out (unmappedNotice)
    // is the most specific, then an expired session. A plain signed_out
    // (never signed in, or a deliberate sign-out) shows no notice.
    const notice =
      unmappedNotice ??
      (sessionExpired
        ? "Your session expired. Sign in again and we'll bring you right back to where you were."
        : null);
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{
          from: location.pathname + location.search,
          notice,
        }}
      />
    );
  }
  // Defensive: user is somehow signed_in but the mapping check is
  // still pending the signOut effect. Render nothing until the
  // effect completes.
  if (!user?.employee_role_slug) {
    return <LoadingBlock>Signing out…</LoadingBlock>;
  }
  return <>{children}</>;
}

// Every authenticated route renders inside PageLayout so the TopNav,
// skip-to-main link, and max-width content area are consistent.
// The helper keeps the per-route JSX compact.
function Authed({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <MfaGate>
        <UserAvatarProvider>
          <PageLayout>{children}</PageLayout>
        </UserAvatarProvider>
      </MfaGate>
    </RequireAuth>
  );
}

// Unknown URL → home, with a toast explaining why (audit 8.1: the
// silent redirect made typos look like the app teleported you).
function UnknownRouteRedirect() {
  const location = useLocation();
  const notice = `That page doesn't exist (${location.pathname}) — you're back on your home screen.`;
  return <Navigate to={`/?notice=${encodeURIComponent(notice)}`} replace />;
}

// MFA enforcement gate. Per the security posture, every employee must
// have 2FA enabled. Until they do, every authenticated route sends them
// to /security to enroll — the one page exempt from the gate. The
// SecurityPage calls useMfa().refresh() after enrollment so the gate
// releases without a reload. While status is still unknown we show the
// boot skeleton; a fetch error fails open (see MfaContext) so a transient
// blip never traps the whole app.
function MfaGate({ children }: { children: React.ReactNode }) {
  const { enabled } = useMfa();
  const location = useLocation();
  if (location.pathname === "/security") return <>{children}</>;
  if (enabled === null) return <AppBootSkeleton />;
  if (enabled === false) {
    return <Navigate to="/security" replace state={{ mfaRequired: true }} />;
  }
  return <>{children}</>;
}

// Service Records PR 20 — route the bare "/" path to the
// role-appropriate landing page. SessionProvider has populated
// `user` by the time RequireAuth lets us render; if it hasn't yet
// (still loading), `user` is null and `roleHomePath` falls through
// to /dashboard, which is the safe default for back_office +
// unknown roles. The Navigate component handles the redirect.
function RoleHomeRedirect() {
  const { user } = useSession();
  const path = roleHomePath(user);
  return <Navigate to={path} replace />;
}

// Forms Platform F1 — the public forms host renders ONLY public form
// routes: no OS shell, no SessionProvider (no auth calls at all from a
// client's browser), and any unknown path lands on a quiet branded
// dead-end instead of the sign-in page.
function PublicFormsApp() {
  return (
    <BrowserRouter>
      <Suspense fallback={<AppBootSkeleton />}>
        <Routes>
          <Route path="/f/t/:token" element={<FormPublicPage />} />
          <Route path="/f/r/:rtoken" element={<FormPublicPage />} />
          <Route path="/f/:publicSlug" element={<FormPublicPage />} />
          {/* A crash on any public booking page must never render blank
              white (2026-08-17) — the boundary shows the office number
              and names the failure instead. */}
          <Route
            path="/book"
            element={
              <BookErrorBoundary>
                <BookFrontDoorPage />
              </BookErrorBoundary>
            }
          />
          <Route
            path="/book/with/:slug"
            element={
              <BookErrorBoundary>
                <BookWithPage />
              </BookErrorBoundary>
            }
          />
          <Route
            path="/book/i/:token"
            element={
              <BookErrorBoundary>
                <BookInvitePage />
              </BookErrorBoundary>
            }
          />
          <Route
            path="/book/manage/:token"
            element={
              <BookErrorBoundary>
                <BookManagePage />
              </BookErrorBoundary>
            }
          />
          <Route path="/intake/r/:token" element={<IntakeFormPage />} />
          {/* The preference center (row 3.5) answers on the forms hosts too. */}
          <Route path="/preferences/:token" element={<PreferenceCenterPage />} />
          {/* The annual check-in (row 3.7) answers on the forms hosts too. */}
          <Route path="/checkin/:token" element={<CheckInPage />} />
          {/* The household digest's page (row 6.6) answers on the forms hosts too. */}
          <Route path="/digest/:token" element={<DigestPage />} />
          {/* The review ask's private path (row 4.4) answers on the forms hosts too. */}
          <Route path="/review/:token" element={<ReviewAskPage />} />
          {/* The introduction ask (row 4.5) answers on the forms hosts too. */}
          <Route path="/introduce/:token" element={<IntroducePage />} />
          <Route
            path="*"
            element={
              <div
                style={{
                  minHeight: "100vh",
                  display: "grid",
                  placeItems: "center",
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  color: "var(--dark-gray)",
                }}
              >
                <p>This link isn't available. Check the link you were given.</p>
              </div>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export function App() {
  if (isPublicFormsHost(window.location.hostname)) {
    return <PublicFormsApp />;
  }
  return (
    <SessionProvider>
      <MfaProvider>
        <BrowserRouter>
          {/* Where you came from in this tab, for "← Back to …" (2026-10-13).
              Above every page so the memory survives moving between them. */}
          <NavHistoryProvider>
            <ToastProvider>
              {/* Account-level user settings (2026-08-15). Mounted ONCE
            here so every surface reads a user's saved choices from one
            fetch instead of its own. Fetches nothing until the session
            is signed in, so the public routes below cost nothing. */}
              <UserSettingsProvider>
                {/* EmailComposerProvider mounts the global EmailComposer ONCE
            for the whole authenticated app — above CleanupProvider so
            the in-record Cleanup drawer (rendered by CleanupProvider
            itself, as a sibling of the routed pages below, not a
            descendant of any page's PageLayout) can reach
            useEmailComposer() too. RecordPeopleControl's "email this
            person" button crashed the app when opened from that drawer
            until this was hoisted here (2026-09-16 Sentry fix) — it used
            to be mounted one level too low, inside PageLayout, which only
            wraps the routed page content.
            HELP-OK: pure provider-nesting bugfix — no user-visible label,
            button, or flow changed; nothing to update in shared/help/. */}
                <EmailComposerProvider>
                  {/* C1/C2 — the in-record Cleanup entry point. Mounted ONCE
            here so any record-detail page's CleanupActionsButton can
            call useCleanup().openForRecord(...). Renders nothing until
            opened; the drawer itself is the same engine as /cleanup. */}
                  <CleanupProvider>
                    {/* All page components are React.lazy (route-level code-splitting,
            audit PR06). AUTHENTICATED page chunks resolve inside
            PageLayout's own Suspense + error boundary (RouteContent.tsx)
            so the nav chrome never unmounts while a page loads or fails
            (Mylah's stuck-loading trap, 2026-07-01). This outer boundary
            covers what's left: the public routes (sign-in, accept-invite,
            upload, intake) and the first session boot. */}
                    <Suspense fallback={<AppBootSkeleton />}>
                      <Routes>
                        {/* Public routes — no auth gate, no chrome. */}
                        <Route path="/sign-in" element={<SignInPage />} />
                        <Route path="/accept-invite" element={<AcceptInvitePage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        {/* v1.x phase 11.16 (Tier B item 11) — magic-link client
              upload landing page. Client opens this with a fresh
              browser session; no auth required. */}
                        <Route path="/upload/:token" element={<ClientUploadPage />} />
                        {/* Documents tab PR 6b — a document request's no-login page
              for someone outside Stewardship; the link is the key. */}
                        <Route path="/request/:token" element={<RequestLinkPage />} />
                        {/* Joy PR 14 — the public intake form (insrenewal.stewardship.pro
              serves this same route; the link is the credential). */}
                        <Route path="/intake/r/:token" element={<IntakeFormPage />} />
                        {/* Forms Platform F1 — hosted public form pages. Served on every
              host; forms.stewardship.pro serves ONLY these (see the
              hostname gate below). */}
                        <Route path="/f/t/:token" element={<FormPublicPage />} />
                        <Route path="/f/r/:rtoken" element={<FormPublicPage />} />
                        <Route path="/f/:publicSlug" element={<FormPublicPage />} />
                        {/* E-signature send half (Phase 1) — the client-facing
                        signing page. No dedicated domain (unlike forms);
                        served on the main app origin only. */}
                        {/* MUST precede /sign/:token — "complete" is the
                        provider's redirect target, not a token. */}
                        <Route path="/sign/complete" element={<EsignCompleteBridgePage />} />
                        <Route path="/sign/:token" element={<EsignSignPage />} />
                        {/* Marketing Hub: "Ask the one question". A family's answer
                        page; no sign-in, the unguessable link is the key.
                        Opening it never writes; the button press does. */}
                        <Route path="/q/:token" element={<AnswerQuestionPage />} />
                        {/* Marketing Hub row 3.5: the preference center, from every
                        bulk footer. No sign-in; the signed link names one
                        person. Opening it never writes; Save does. */}
                        <Route path="/preferences/:token" element={<PreferenceCenterPage />} />
                        {/* Marketing Hub row 3.7: the annual check-in. No sign-in;
                        the signed, dated link names one person. Opening it
                        never writes; Send my answers does. */}
                        <Route path="/checkin/:token" element={<CheckInPage />} />
                        {/* Marketing Hub row 6.6: the page behind the household
                        digest. No sign-in; the signed, per-month link names
                        one person. Opening it never writes; a tap does. */}
                        <Route path="/digest/:token" element={<DigestPage />} />
                        {/* Marketing Hub row 4.4: the review ask's private page.
                        No sign-in; the per-person link is the key. Opening it
                        never writes; the tap does. */}
                        <Route path="/review/:token" element={<ReviewAskPage />} />
                        {/* Marketing Hub row 4.5: the introduction ask's page. No
                        sign-in; the per-person link is the key. Opening it
                        never writes; the tap does. */}
                        <Route path="/introduce/:token" element={<IntroducePage />} />
                        {/* A crash on any public booking page must never render blank
                        white (2026-08-17) — the boundary shows the office number
                        and names the failure instead. */}
                        <Route
                          path="/book"
                          element={
                            <BookErrorBoundary>
                              <BookFrontDoorPage />
                            </BookErrorBoundary>
                          }
                        />
                        <Route
                          path="/book/with/:slug"
                          element={
                            <BookErrorBoundary>
                              <BookWithPage />
                            </BookErrorBoundary>
                          }
                        />
                        <Route
                          path="/book/i/:token"
                          element={
                            <BookErrorBoundary>
                              <BookInvitePage />
                            </BookErrorBoundary>
                          }
                        />
                        <Route
                          path="/book/manage/:token"
                          element={
                            <BookErrorBoundary>
                              <BookManagePage />
                            </BookErrorBoundary>
                          }
                        />

                        {/* Service Records PR 20 — role-based home routing.
              Post-login lands on the page tuned to the user's role:
                advisor / company_admin → /my-day
                system_admin / compliance → /dashboard (Ops merged in)
                front_desk → /donna
                back_office / unknown → /dashboard
              See web/src/utils/role-home.ts for the full table. */}
                        <Route
                          path="/"
                          element={
                            <RequireAuth>
                              <RoleHomeRedirect />
                            </RequireAuth>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <Authed>
                              <DashboardPage />
                            </Authed>
                          }
                        />
                        {/* Ambassador program PR 2 — the ambassador's one surface.
              RequireRole is UX; the /api/ambassador/* handlers enforce
              the role server-side, and the deny-by-default route gate
              stops ambassadors reaching anything else. */}
                        <Route
                          path="/referrals"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={(u) => u.employee_role_slug === "ambassador"}
                                pageName="Referrals"
                              >
                                <AmbassadorReferralsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Ambassador program PR 5 — free/busy for Nate and Joey,
              plus booking for one of the ambassador's own referrals. */}
                        <Route
                          path="/availability"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={(u) => u.employee_role_slug === "ambassador"}
                                pageName="Advisor Time"
                              >
                                <AmbassadorAvailabilityPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Forms Platform F2 — the builder + Forms section (all employees). */}
                        <Route
                          path="/forms"
                          element={
                            <Authed>
                              <FormsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/forms/:slug"
                          element={
                            <Authed>
                              <FormBuilderPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/forms/:slug/submissions"
                          element={
                            <Authed>
                              <FormSubmissionsPage />
                            </Authed>
                          }
                        />
                        {/* PR3 (2026-09-06): the single shared "Form Results"
                        view, reachable identically from the appointment,
                        the household, and the contact. */}
                        <Route
                          path="/forms/submissions/:id"
                          element={
                            <Authed>
                              <FormResultsPage />
                            </Authed>
                          }
                        />
                        {/* v1.x phase 11.16 (Tier B item 8) — Donna's front-desk
              concierge dashboard. Accessible to anyone signed in;
              the page itself is most useful for Donna's role but
              system_admin / compliance often want to peek too. */}
                        <Route
                          path="/dashboard/donna"
                          element={
                            <Authed>
                              <DonnaDashboardPage />
                            </Authed>
                          }
                        />
                        {/* Donna, 2026-08-04 — click an advisor on the front-desk
              board to open their day, their Bookings links, and (front
              desk / admins) a form that books the appointment here.
              Server-side gate lives on /api/advisors/*; this route is
              reachable by any signed-in user, who then sees only what
              their role allows. */}
                        <Route
                          path="/advisors/:userId/schedule"
                          element={
                            <Authed>
                              <AdvisorSchedulePage />
                            </Authed>
                          }
                        />
                        {/* Role-dashboard archetype A — Relationship Briefing.
              Preview route; PR D wires per-person dispatch so the right
              advisors land here as their home. Placed before
              /dashboard/:company so "briefing" isn't captured as a slug. */}
                        <Route
                          path="/dashboard/briefing"
                          element={
                            <Authed>
                              <BriefingDashboard />
                            </Authed>
                          }
                        />
                        {/* Role-dashboard archetype B — Operational Cockpit. Preview
              route; PR D wires per-person dispatch. */}
                        <Route
                          path="/dashboard/cockpit"
                          element={
                            <Authed>
                              <CockpitDashboard />
                            </Authed>
                          }
                        />
                        <Route
                          path="/dashboard/:company"
                          element={
                            <Authed>
                              <CompanyDashboardPage />
                            </Authed>
                          }
                        />
                        {/* Unified Cleanup System — core OS functionality for EVERY
              employee (RequireAuth via <Authed>, NOT admin-gated).
              Per-issue/per-action company-scope authz is enforced
              server-side. */}
                        {/* 2026-09-11 (Grant): Cleanup and Data Needed became ONE
              surface — /data-needed. The workspace's issue deep-links
              (?issue=<id>) carry over so bookmarks and the in-record
              "More →" links still land on the card. */}
                        <Route path="/cleanup" element={<CleanupToDataNeeded />} />
                        {/* C4 — the one-click glossary (UX rule #9). Static path,
              same RequireAuth gate as the workspace. */}
                        <Route
                          path="/cleanup/help"
                          element={
                            <Authed>
                              <CleanupHelpPage />
                            </Authed>
                          }
                        />
                        {/* The Data Accuracy sprint board was the PRE-CUTOVER cleanup
              scoreboard (burndown toward "everybody green", leaderboard,
              days-until-cutover). Removed at cutover, 2026-07-31 — the
              sprint it measured is over. Data Needed is the durable home
              for a person's cleanup + judgment work, so old links and
              bookmarks land there instead of dead-ending. */}
                        <Route
                          path="/data-accuracy"
                          element={<Navigate to="/data-needed" replace />}
                        />
                        {/* Upcoming automated communications drill-in (2026-07-03) —
              the list behind the dashboard tile's three numbers. All
              employees (cross-creator visibility doctrine). */}
                        <Route
                          path="/upcoming-communications"
                          element={
                            <Authed>
                              <UpcomingCommunicationsPage />
                            </Authed>
                          }
                        />
                        {/* "What's running on my book?" (2026-08-02) — read-only and
              cross-creator, so every employee may open it: knowing what
              acts on your client is not an authoring permission. */}
                        <Route
                          path="/automations/on-my-clients"
                          element={
                            <Authed>
                              <AutomationsOnMyClientsPage />
                            </Authed>
                          }
                        />
                        {/* Data Needed inbox (PR7) — the per-user worklist. */}
                        <Route
                          path="/data-needed"
                          element={
                            <Authed>
                              <DataNeededPage />
                            </Authed>
                          }
                        />
                        {/* S3 PR 6 — judgment-completion team scoreboard. ALL
              employees (transparency doctrine; decision E). */}
                        <Route
                          path="/data-needed/team"
                          element={
                            <Authed>
                              <JudgmentTeamPage />
                            </Authed>
                          }
                        />
                        {/* Security / 2FA enrollment (MFA PR2). Every employee. */}
                        <Route
                          path="/security"
                          element={
                            <Authed>
                              <SecurityPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/contacts"
                          element={
                            <Authed>
                              <ContactsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/contacts/:id"
                          element={
                            <Authed>
                              <ContactPage />
                            </Authed>
                          }
                        />
                        {/* Client Snapshot (PR 10) — the one-screen "everything I need on
              the phone" view. Entry buttons that link here are Ryan-only
              (SnapshotButton self-gates); the route is a read-only
              aggregation of data already visible on the full record. */}
                        <Route
                          path="/contacts/:id/snapshot"
                          element={
                            <Authed>
                              <ClientSnapshotPage entity="contact" />
                            </Authed>
                          }
                        />
                        <Route
                          path="/households"
                          element={
                            <Authed>
                              <HouseholdsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/households/:id"
                          element={
                            <Authed>
                              <HouseholdPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/trusts/:id"
                          element={
                            <Authed>
                              <TrustPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/households/:id/snapshot"
                          element={
                            <Authed>
                              <ClientSnapshotPage entity="household" />
                            </Authed>
                          }
                        />
                        {/* S4 — requote worksheet (andrea-planning-ops.md §4
              step 3): a read-only assembly of who's covered, current
              plan, doctors/meds, and income for one household. */}
                        <Route
                          path="/households/:id/requote-worksheet"
                          element={
                            <Authed>
                              <PlanningRequoteWorksheetPage />
                            </Authed>
                          }
                        />
                        {/* The ACA quote: Andrea's plan comparison for one ACA
              case and plan year. */}
                        <Route
                          path="/aca-quote/:kind/:id"
                          element={
                            <Authed>
                              <AcaQuotePage />
                            </Authed>
                          }
                        />
                        {/* The Medicare quote (Quote tab step 3): Andrea's
              plan comparison for one Medicare case and plan year. */}
                        <Route
                          path="/medicare-quote/:kind/:id"
                          element={
                            <Authed>
                              <MedicareQuotePage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/households/:id/companies/:slug"
                          element={
                            <Authed>
                              <HouseholdCompanyPage />
                            </Authed>
                          }
                        />
                        {/* PR3 of the data-hygiene revamp — clickable service-record
              detail page (Managed Portfolio, Home Policy, etc.).
              Replaces the previously-static cards in the cross-company
              services band + per-company drilldown. */}
                        {/* Service Records PR 5a — cross-company SR list with text
              search across SR + subtype-table fields. Lands before
              the /:id detail route so /service-records (no id) matches
              the list. */}
                        <Route
                          path="/service-records"
                          element={
                            <Authed>
                              <ServiceRecordsListPage />
                            </Authed>
                          }
                        />
                        {/* Services Check book-wide list (Jake Norton,
                        2026-09-12) — reachable from a link on the
                        Services list page, not its own top-nav item
                        (the six-item nav bar is deliberately full, see
                        top-nav-helpers.ts). */}
                        <Route
                          path="/services-check"
                          element={
                            <Authed>
                              <ServicesCheckListPage />
                            </Authed>
                          }
                        />
                        {/* Marketing Hub Today (row 2.3a). Reached from the ⌘K
                        quick-pick, not its own top-nav item: the six-item
                        bar is full (top-nav-helpers.ts). Company scope is
                        enforced by the API, not by this route. */}
                        <Route
                          path="/marketing/today"
                          element={
                            <Authed>
                              <MarketingTodayPage />
                            </Authed>
                          }
                        />
                        {/* Where we could help (Marketing Hub row 3.4). Every
                        employee reads it; the API decides who may set a
                        goal (the gap's own company desk). */}
                        <Route
                          path="/marketing/opportunities"
                          element={
                            <Authed>
                              <MarketingOpportunitiesPage />
                            </Authed>
                          }
                        />
                        {/* Referrals (Marketing Hub row 4.2): the proven engine as
                        a work list. Every employee reads their companies'
                        introductions; the API decides who may act. */}
                        <Route
                          path="/marketing/referrals"
                          element={
                            <Authed>
                              <MarketingReferralsPage />
                            </Authed>
                          }
                        />
                        {/* The unified inbox (Marketing Hub row 5.5, screen 20):
                        every reply, comment, message, review and introduction
                        in one queue with an owner and the four-hour clock.
                        Every employee; the API scopes it to their desk. */}
                        <Route
                          path="/marketing/inbox"
                          element={
                            <Authed>
                              <MarketingInboxPage />
                            </Authed>
                          }
                        />
                        {/* Events (Marketing Hub row 8.10): the list and one event
                        with its funnel, plan, brief and door. Every employee reads
                        their companies'; the API decides who acts. */}
                        <Route
                          path="/marketing/events"
                          element={
                            <Authed>
                              <MarketingEventsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/events/:id"
                          element={
                            <Authed>
                              <MarketingEventPage />
                            </Authed>
                          }
                        />
                        {/* Mail and cards (Marketing Hub row 8.11): the tasks a
                        mail or card piece became, with the address, the words
                        and "Mailed". Every employee; the API decides who acts. */}
                        <Route
                          path="/marketing/mail"
                          element={
                            <Authed>
                              <MarketingMailPage />
                            </Authed>
                          }
                        />
                        {/* Email my clients (2026-10-23): the one door under
                        Marketing for emailing many families, a chooser between
                        the Outlook paced send and the SES blast. Sends nothing. */}
                        <Route
                          path="/marketing/email-clients"
                          element={
                            <Authed>
                              <MarketingEmailClientsPage />
                            </Authed>
                          }
                        />
                        {/* Google reviews (Marketing Hub row 8.9): every review on
                        the firm's Business Profile, answered through Grant's
                        approval. Every employee; the API decides who may draft. */}
                        <Route
                          path="/marketing/reviews"
                          element={
                            <Authed>
                              <MarketingReviewsPage />
                            </Authed>
                          }
                        />
                        {/* The advisor share packages (Marketing Hub row 5.6):
                        the Advisor share piece of each package Grant
                        accepted, for the advisors of its company. Every
                        employee; the API clips it to their desk. */}
                        <Route
                          path="/marketing/studio/share"
                          element={
                            <Authed>
                              <MarketingSharePage />
                            </Authed>
                          }
                        />
                        {/* Results (Marketing Hub row 7.10a, screen 9): each goal
                        against its brief, then every campaign, series, piece
                        and channel. Every employee; the API scopes to the
                        viewer's companies, one click to widen. */}
                        <Route
                          path="/marketing/results"
                          element={
                            <Authed>
                              <MarketingResultsPage />
                            </Authed>
                          }
                        />
                        {/* The team leaderboard (Marketing Hub row 7.11b, screen 9a):
                        every sale for the person who worked it. Every employee;
                        the API scopes to the viewer's companies, one click to
                        widen. */}
                        <Route
                          path="/marketing/leaderboard"
                          element={
                            <Authed>
                              <MarketingLeaderboardPage />
                            </Authed>
                          }
                        />
                        {/* Results · Content (Marketing Hub row 5.7, screen 10):
                        every account by conversations, hand-raises and
                        outcomes. Every employee; the API scopes the rows. */}
                        {/* Split 2 audit M14: Content is a tab of Results now; the old address opens it. */}
                        <Route
                          path="/marketing/results/content"
                          element={<Navigate to="/marketing/results?tab=content" replace />}
                        />
                        {/* The Referral Network report (row 4.6): where business
                        comes from and what the referral network produced.
                        Every employee reads their own companies; Grant the
                        firm. Moved here from /admin on 2026-10-09. */}
                        <Route
                          path="/marketing/referral-network"
                          element={
                            <Authed>
                              <ReferralNetworkPage />
                            </Authed>
                          }
                        />
                        {/* The financial brief's settings (row 2.7b): Economics,
                        Benchmarks, Desk capacity. Everyone reads their
                        companies' rows; the API lets only Grant save. */}
                        {/* Founder Studio (row 5.2): Grant by user id; the API
                        refuses everyone else and the page prints the sentence. */}
                        <Route
                          path="/marketing/studio/founder"
                          element={
                            <Authed>
                              <FounderStudioPage />
                            </Authed>
                          }
                        />
                        {/* Campaigns and series (row 7.5, screens 11 and 12).
                        Everyone on the team reads; the API lets only the
                        company's desk or Grant change them, and only Grant
                        approve a series. Nothing on these pages sends. */}
                        <Route
                          path="/marketing/campaigns"
                          element={
                            <Authed>
                              <MarketingCampaignsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/campaigns/:id"
                          element={
                            <Authed>
                              <MarketingCampaignPage />
                            </Authed>
                          }
                        />
                        {/* Goals (row 7.8a, screen 8). Everyone reads; the API
                            lets only the family's desk or Grant prepare, and
                            only Grant approve. Nothing on these pages sends. */}
                        <Route
                          path="/marketing/goals/new"
                          element={
                            <Authed>
                              <MarketingGoalPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/goals/:id"
                          element={
                            <Authed>
                              <MarketingGoalPage />
                            </Authed>
                          }
                        />
                        {/* The approval queue (row 7.9a). Grant acts; Baker
                            reads; Jake and Ryan see their own sign-offs (the
                            API checks each by user id). */}
                        <Route
                          path="/marketing/approvals"
                          element={
                            <Authed>
                              <MarketingApprovalsPage />
                            </Authed>
                          }
                        />
                        {/* Growth levels (row 7.8b). Everyone reads; only Grant
                            changes a level (the API checks his user id). */}
                        <Route
                          path="/marketing/levels"
                          element={
                            <Authed>
                              <MarketingLevelsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/levels/:company/:family"
                          element={
                            <Authed>
                              <MarketingLevelsPage />
                            </Authed>
                          }
                        />
                        {/* Playbooks and outside research (row 11.3). Everyone reads;
                            the owner or Grant writes, only Grant approves (the API checks). */}
                        <Route
                          path="/marketing/playbooks"
                          element={
                            <Authed>
                              <MarketingPlaybooksPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/playbooks/:company/:family"
                          element={
                            <Authed>
                              <MarketingPlaybooksPage />
                            </Authed>
                          }
                        />
                        {/* Approved plays (row 11.6). Everyone reads; the owner or Grant
                            writes, only Grant approves, the company's desk launches (the API checks). */}
                        <Route
                          path="/marketing/plays"
                          element={
                            <Authed>
                              <MarketingPlaysPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/plays/:id"
                          element={
                            <Authed>
                              <MarketingPlaysPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/series/:id"
                          element={
                            <Authed>
                              <MarketingSeriesPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/pieces/:id"
                          element={
                            <Authed>
                              <MarketingPiecePage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/posts"
                          element={
                            <Authed>
                              <MarketingPostsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/calendar"
                          element={
                            <Authed>
                              <MarketingCalendarPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/content"
                          element={
                            <Authed>
                              <MarketingContentPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/settings"
                          element={
                            <Authed>
                              <MarketingSettingsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/marketing/settings/:section"
                          element={
                            <Authed>
                              <MarketingSettingsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/service-records/:id"
                          element={
                            <Authed>
                              <ServiceRecordDetailPage />
                            </Authed>
                          }
                        />

                        {/* gs follow-ons A1 — the vendors reference surface. Global
              search + defaultEntityHref have linked /vendors/:id since
              S2; these routes close the silent-redirect gap. */}
                        {/* 2026-08-03 — the browsable help guide. Same corpus Ask
              Stew answers "how do I…?" from (shared/help/), viewer-
              filtered inside the page. Every employee. */}
                        <Route
                          path="/help"
                          element={
                            <Authed>
                              <HelpPage />
                            </Authed>
                          }
                        />
                        {/* Jean/Baker email, 2026-08-28: Donna and front-desk
                        staff need to show a licensee's current license on
                        screen if asked in person. Read only, no
                        correct/renew/retire — every employee, same as
                        /help. */}
                        <Route
                          path="/all-pages"
                          element={
                            <Authed>
                              <AllPagesPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/license-lookup"
                          element={
                            <Authed>
                              <LicenseLookupPage />
                            </Authed>
                          }
                        />
                        {/* PM Guidelines — Mike's Private Money underwriting
                        numbers. Reached from Company tools → Mortgage, and only shown
                        there to mortgage-scoped users; the server checks the
                        same scope on every request, so this route being
                        reachable by URL is not a leak (plan §6.3). */}
                        <Route
                          path="/pm-guidelines"
                          element={
                            <Authed>
                              <PmGuidelinesPage />
                            </Authed>
                          }
                        />
                        {/* The underwriting report for one Private Money loan.
                        The subject is the Opportunity being quoted or the
                        Processing record being delivered — a report can be
                        run at either point. Mortgage scope, server-checked
                        on every request. */}
                        <Route
                          path="/pm-underwriting/:subjectType/:subjectId"
                          element={
                            <Authed>
                              <PmUnderwritingPage />
                            </Authed>
                          }
                        />
                        {/* KlinkLoans' fund and where it sits. Deliberately a
                        separate screen from PM Guidelines: a Limit is a rule
                        Mike sets, a reading is a fact from their upload, and
                        one page would blur them (plan §6.3). */}
                        <Route
                          path="/pm-fund"
                          element={
                            <Authed>
                              <PmFundPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/vendors"
                          element={
                            <Authed>
                              <VendorsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/vendors/:id"
                          element={
                            <Authed>
                              <VendorDetailPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/employers"
                          element={
                            <Authed>
                              <EmployersPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/employers/:id"
                          element={
                            <Authed>
                              <EmployerDetailPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/employers/:id/group-plans/:planId"
                          element={
                            <Authed>
                              <GroupPlanPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/employers/:id/group-plans/:planId/bring-in"
                          element={
                            <Authed>
                              <GroupPlanBringInPage />
                            </Authed>
                          }
                        />

                        {/* Vocabulary v1.4 surfaces — Opportunities / Processing /
              Ongoing Care. Renamed from /sales + /in-progress in
              v1.4 (was the v1.0–v1.3 naming "Sales / In Progress /
              Ongoing Care"). Old paths 302-redirect so existing
              bookmarks land on meaningful destinations. */}
                        <Route
                          path="/opportunities"
                          element={
                            <Authed>
                              <OpportunitiesPage />
                            </Authed>
                          }
                        />
                        {/* Service Records PR 8a — Kanban view of opportunities.
              Sibling to /opportunities (the Table view); both consume
              the same /api/opportunities/list endpoint. Mounted BEFORE
              /opportunities/:id so the literal "board" segment beats
              the parametric :id matcher. */}
                        <Route
                          path="/opportunities/board"
                          element={
                            <Authed>
                              <OpportunityBoardPage />
                            </Authed>
                          }
                        />
                        {/* Service Records PR 16 — Aging + Forecast views.
              Mounted BEFORE /opportunities/:id so the literal
              "aging" / "forecast" segments beat the parametric :id. */}
                        <Route
                          path="/opportunities/aging"
                          element={
                            <Authed>
                              <OpportunityAgingPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/opportunities/forecast"
                          element={
                            <Authed>
                              <OpportunityForecastPage />
                            </Authed>
                          }
                        />
                        {/* v1.x phase 11.16 — standalone Opportunity detail page.
              Rendered when the user clicks a row in the
              OpportunitiesPage list view. */}
                        <Route
                          path="/opportunities/:id"
                          element={
                            <Authed>
                              <OpportunityDetailPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/processing"
                          element={
                            <Authed>
                              <ProcessingPage />
                            </Authed>
                          }
                        />
                        {/* Joey's Processing board — Kanban view of active transactions
              with drag-to-move stage transitions. Sibling to /processing
              (the Table view); both consume /api/transactions/list.
              Mounted BEFORE /processing/:id so the literal "board" segment
              beats the :id wildcard. */}
                        <Route
                          path="/processing/board"
                          element={
                            <Authed>
                              <ProcessingBoardPage />
                            </Authed>
                          }
                        />
                        {/* Editable pipeline ("board") templates. Reachable by any
              employee; per-pipeline edit authority is company-scoped and
              enforced both in the page and server-side. */}
                        <Route
                          path="/admin/pipeline-boards"
                          element={
                            <Authed>
                              <PipelineBoardsAdminPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/esign"
                          element={
                            <Authed>
                              <EsignPage />
                            </Authed>
                          }
                        />
                        {/* Personal e-sign settings — signature, initials,
              send defaults. Static segment, so React Router ranks it
              above /esign/:envelopeId regardless of order; declared
              first anyway for the reader. */}
                        <Route
                          path="/esign/settings"
                          element={
                            <Authed>
                              <EsignSettingsPage />
                            </Authed>
                          }
                        />
                        {/* Industry Documents (2026-09-02) — one company's
              blank-form library, or the viewer's own with no company
              in the URL yet (the page redirects to its default). */}
                        <Route
                          path="/esign/industry-documents"
                          element={
                            <Authed>
                              <IndustryDocumentsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/esign/industry-documents/:company"
                          element={
                            <Authed>
                              <IndustryDocumentsPage />
                            </Authed>
                          }
                        />
                        {/* Templates (2026-09-03) — the saved, ordered
              PACKAGES built from Industry documents. With no company in
              the URL the page redirects to the viewer's own ("default
              to your desk"). */}
                        <Route
                          path="/esign/templates"
                          element={
                            <Authed>
                              <EsignTemplatesPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/esign/templates/:company"
                          element={
                            <Authed>
                              <EsignTemplatesPage />
                            </Authed>
                          }
                        />
                        {/* What the OS has learned about a company's form
              captions (2026-09-15), and how to take back a wrong
              lesson. Static segments, so both rank above
              /esign/:envelopeId. */}
                        <Route
                          path="/esign/learned"
                          element={
                            <Authed>
                              <EsignLearnedCaptionsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/esign/learned/:company"
                          element={
                            <Authed>
                              <EsignLearnedCaptionsPage />
                            </Authed>
                          }
                        />
                        {/* One envelope: every signer's journey, the files,
              the history, and the two actions. */}
                        <Route
                          path="/esign/:envelopeId"
                          element={
                            <Authed>
                              <EsignEnvelopePage />
                            </Authed>
                          }
                        />
                        {/* v1.x phase 11.16 — standalone Processing/Transaction
              detail page. */}
                        <Route
                          path="/processing/:id"
                          element={
                            <Authed>
                              <TransactionDetailPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/ongoing-care"
                          element={
                            <Authed>
                              <OngoingCareHome />
                            </Authed>
                          }
                        />
                        {/* PR 6: the year calendar is now the DEFAULT view of
              /ongoing-care. The old standalone calendar deep-link
              redirects to that view so bookmarks keep working. */}
                        <Route
                          path="/ongoing-care/calendar"
                          element={<Navigate to="/ongoing-care?view=calendar" replace />}
                        />
                        {/* PR 13: Month drill-down. Static "month" segment ranks above /:id. */}
                        <Route
                          path="/ongoing-care/month/:ym"
                          element={
                            <Authed>
                              <MonthDrilldownPage />
                            </Authed>
                          }
                        />
                        {/* Retired single-household wizard (v2 step 4): see
              RetiredNewRhythmRedirect. */}
                        <Route
                          path="/ongoing-care/new"
                          element={
                            <Authed>
                              <RetiredNewRhythmRedirect />
                            </Authed>
                          }
                        />
                        {/* User-owned plan management (2026-07-23): the plans list,
              the plan-creation form, and each plan's manage page.
              Static "plans" segment ranks above /:id. */}
                        <Route
                          path="/ongoing-care/plans"
                          element={
                            <Authed>
                              <CarePlanDefinitionsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/ongoing-care/plans/new"
                          element={
                            <Authed>
                              <NewCarePlanDefinitionPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/ongoing-care/plans/:defId"
                          element={
                            <Authed>
                              <CarePlanDefinitionPage />
                            </Authed>
                          }
                        />
                        {/* Care-plan detail (QA BUG-030): a rhythm-instance row now has
              its own page instead of routing to the parent household. */}
                        <Route
                          path="/ongoing-care/:id"
                          element={
                            <Authed>
                              <RhythmDetailPage />
                            </Authed>
                          }
                        />
                        {/* PR 9: Edit Plan. More-specific than /:id, so it wins. */}
                        <Route
                          path="/ongoing-care/:id/edit"
                          element={
                            <Authed>
                              <EditRhythmPage />
                            </Authed>
                          }
                        />
                        {/* Old vocabulary v1.0–v1.3 paths kept as 302 redirects so
              external bookmarks, pasted links in Slack, and the
              previously-shipped Sidebar shortcut deep-links keep
              working. Drop these once we're confident no advisor
              still has them bookmarked (v1.1 / post-cutover). */}
                        <Route path="/transactions/:id" element={<TransactionToProcessing />} />
                        <Route path="/sales" element={<Navigate to="/opportunities" replace />} />
                        <Route path="/sales/*" element={<Navigate to="/opportunities" replace />} />
                        <Route
                          path="/in-progress"
                          element={<Navigate to="/processing" replace />}
                        />
                        <Route
                          path="/in-progress/*"
                          element={<Navigate to="/processing" replace />}
                        />
                        <Route path="/deals" element={<Navigate to="/opportunities" replace />} />
                        <Route path="/deals/*" element={<Navigate to="/opportunities" replace />} />
                        <Route path="/pipelines" element={<Navigate to={`/dashboard?notice=${encodeURIComponent("Pipelines moved. Your home screen has your work, and Opportunities and Processing are in the left menu.")}`} replace />} />
                        <Route path="/pipelines/*" element={<Navigate to={`/dashboard?notice=${encodeURIComponent("Pipelines moved. Your home screen has your work, and Opportunities and Processing are in the left menu.")}`} replace />} />
                        {/* Shorthand / convention URLs that previously fell through to
              the catch-all and landed on /admin/ops (QA BUG-032). Point
              them at their real destinations instead. */}
                        <Route path="/founder" element={<Navigate to={`/dashboard?notice=${encodeURIComponent("The founder page is now your home screen.")}`} replace />} />
                        <Route path="/admin/reports" element={<Navigate to="/reports" replace />} />
                        <Route
                          path="/admin/templates"
                          element={<Navigate to="/admin/communication-templates" replace />}
                        />

                        {/* Admin-only top-level item. */}
                        <Route
                          path="/automations"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAutomations} pageName="Automations">
                                <AutomationsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* "What runs by itself" (2026-08-11) — every employee,
              same gate as Automations itself (canSeeAutomations: any
              signed-in employee with a role). */}
                        <Route
                          path="/system-jobs"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAutomations}
                                pageName="What Runs Automatically"
                              >
                                <SystemJobsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Service Records PR 12 — Automation Workspace home +
              detail. Reachable from /automations or directly via
              the in-record drawer's links. */}
                        <Route
                          path="/admin/automations"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAutomations} pageName="Automations">
                                <AutomationsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* PR 15 — Builder wizard. Mounted BEFORE the :id route so
              the literal `new` segment matches as a route, not as an
              automation id. */}
                        <Route
                          path="/admin/automations/new"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAutomations} pageName="Automations">
                                <AutomationBuilderPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* One screen per rule (2026-08-29 design audit): a rule's
              page IS its editor — viewing, editing, and turning it on
              all happen here, with the management rail (owner/scope/
              history/runs) below. /edit is kept only so old links/
              bookmarks still land somewhere; it's the exact same page
              as the bare :id route now. */}
                        <Route
                          path="/admin/automations/:id/edit"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAutomations} pageName="Automations">
                                <AutomationBuilderPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/automations/:id"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAutomations} pageName="Automations">
                                <AutomationBuilderPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Service Records PR 17 — Workload Planner monthly capacity.
              Same role-gate as Automations (system_admin + compliance). */}
                        <Route
                          path="/admin/workload-planner"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAutomationsNav}
                                pageName="Workload Planner"
                              >
                                <WorkloadPlannerPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Service Records PR 20 — role-based landing pages.
              /my-day for advisor + company_admin
              /dashboard for system_admin + compliance (Ops merged in)
              See web/src/utils/role-home.ts for the routing table. */}
                        <Route
                          path="/my-day"
                          element={
                            <Authed>
                              <MyDayPage />
                            </Authed>
                          }
                        />
                        {/* Ops Dashboard retired 2026-06-21 — merged into the CEO
              Cockpit at /dashboard (pre-cutover sprint + automation
              health + admin tools now live there). Redirect preserves
              old bookmarks. */}
                        <Route path="/admin/ops" element={<Navigate to={`/dashboard?notice=${encodeURIComponent("Admin Ops moved to your home screen.")}`} replace />} />
                        {/* Service Records PR 19 — Communication templates library.
              system_admin manages the catalog; advisors consume via
              the EmailComposer picker + automations reference by slug. */}
                        <Route
                          path="/admin/communication-templates"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAutomationsNav}
                                pageName="Communication Templates"
                              >
                                <CommunicationTemplatesPage />
                              </RequireRole>
                            </Authed>
                          }
                        />

                        {/* Communications Hub — the bulk (Amazon SES) blast composer.
              Open to EVERY employee (Grant 2026-06-08): the send endpoints
              are requireAuth server-side. Safety is structural — only
              marketing-opted-in, non-suppressed contacts are reached, each
              send carries one-click unsubscribe, and the composer confirms
              the recipient count before sending. 1:1 + relational mail is
              the separate /email surface. */}
                        <Route
                          path="/communications"
                          element={
                            <Authed>
                              <CommunicationsPage />
                            </Authed>
                          }
                        />
                        {/* Office 365 phase 4: EMAIL surface. Sub-routes are handled
              by EmailPage's nested <Routes> so the folder tree stays
              mounted across folder/message navigation. */}
                        <Route
                          path="/email/*"
                          element={
                            <Authed>
                              <EmailPage />
                            </Authed>
                          }
                        />
                        {/* The OS's saved copy of a logged email, readable by every
              employee (the live /email/messages view only works for the
              mailbox that holds the message). */}
                        <Route
                          path="/saved-email/:activityId"
                          element={
                            <Authed>
                              <SavedEmailPage />
                            </Authed>
                          }
                        />
                        {/* v1.x: Microsoft To Do — Flow B live proxy. Flow A
              (Stewardship-linked tasks via manual_tasks mirror) is
              deferred to a follow-up. */}
                        <Route
                          path="/tasks"
                          element={
                            <Authed>
                              <TasksPage />
                            </Authed>
                          }
                        />
                        {/* Documents tab PR 6a: the drop panel for a document
              request from a teammate (the bell and "Requests for you"
              link here). */}
                        <Route
                          path="/requests/:id"
                          element={
                            <Authed>
                              <RequestPanelPage />
                            </Authed>
                          }
                        />
                        {/* Two-way proxy of the advisor's Microsoft 365
              calendar (Agenda / Week / Month — the original
              read-only 7-day strip grew up). */}
                        <Route
                          path="/calendar"
                          element={
                            <Authed>
                              <CalendarPage />
                            </Authed>
                          }
                        />
                        {/* The Appointments list (calendar-nav fold-in,
              2026-08-25) — who booked with me / the office,
              read from the OS's own appointments table. */}
                        <Route
                          path="/appointments"
                          element={
                            <Authed>
                              <AppointmentsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/reports"
                          element={
                            <Authed>
                              <ReportsPage />
                            </Authed>
                          }
                        />
                        {/* Closed Loan Report (close-out program PR 5) — the
                      hand-typed spreadsheet's live replacement. */}
                        <Route
                          path="/mortgage/closed-loan-report"
                          element={
                            <Authed>
                              <ClosedLoanReportPage />
                            </Authed>
                          }
                        />
                        {/* The auditor's Loan Log (mortgage compliance phase 3) —
                      both loan types, seven columns, rolling 24 months. */}
                        <Route
                          path="/mortgage/loan-log"
                          element={
                            <Authed>
                              <LoanLogPage />
                            </Authed>
                          }
                        />
                        {/* NMLS MCR prep worksheet (NMLS program PR C). */}
                        <Route
                          path="/mortgage/call-report"
                          element={
                            <Authed>
                              <CallReportPage />
                            </Authed>
                          }
                        />
                        {/* Baker's Mortgage Compliance window (mortgage
                      compliance phase 9) — one place holding the three
                      reports above plus the licence file. A launcher, so
                      it is open to every employee exactly as its cards'
                      own routes are; the admin-gated licence card is
                      rendered only for users who can open it. */}
                        {/* Row 10.5: the Mortgage watch. The route is
                      gated by gateInquiryDataAccess; anyone else sees who
                      holds it. */}
                        <Route
                          path="/mortgage/watch"
                          element={
                            <Authed>
                              <MortgageWatchPage />
                            </Authed>
                          }
                        />
                        {/* Client sign-in (Mortgage OS A2a): invite a borrower to
                      my.stewardship.pro. The server gates it to Mortgage;
                      anyone else sees the refusal. */}
                        {/* The Mortgage OS loan file (A1b), opened from the
                      "Open loan file" button on a traditional Mortgage
                      record. The server opens it to Mortgage, compliance
                      and admins; anyone else sees the refusal. */}
                        <Route
                          path="/mortgage/loan-files/:fileId"
                          element={
                            <Authed>
                              <LoanFilePage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/mortgage/client-sign-in"
                          element={
                            <Authed>
                              <ClientSignInPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/mortgage/compliance"
                          element={
                            <Authed>
                              <MortgageCompliancePage />
                            </Authed>
                          }
                        />

                        {/* Sidebar role-shortcut placeholders. */}
                        <Route
                          path="/pulse"
                          element={
                            <Authed>
                              <PulsePage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/watchlist"
                          element={
                            <Authed>
                              <WatchlistPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/trends"
                          element={
                            <Authed>
                              <TrendsPage />
                            </Authed>
                          }
                        />

                        {/* Saved views — placeholder per-slug page until the saved-
              views backend ships (cutover-checklist §1). */}
                        <Route
                          path="/saved-views/:slug"
                          element={
                            <Authed>
                              <SavedViewPage />
                            </Authed>
                          }
                        />

                        {/* Admin section — gated by canSeeAdminSection (system_admin
              + compliance) at the route layer; the /admin/team
              sub-page tightens further to system_admin only. */}
                        <Route
                          path="/admin/team"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeTeamPage} pageName="Team">
                                <AdminTeamTabbedPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Ambassador program PR 6 — the referral-fee ledger and the
              editable program rules. RequireRole is UX; the
              /api/admin/ambassador-* routes require system_admin. */}
                        <Route
                          path="/admin/ambassador-fees"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={(u) => u.employee_role_slug === "system_admin"}
                                pageName="Ambassador Fees"
                              >
                                <AdminAmbassadorFeesPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* E3 cutover (2026-05-18): the Phase-8 Reconciliation hub
              is replaced by /cleanup (hh-match-quarantine + spouse-pass
              + trust-reattach detectors). Old URL 302-redirects so
              bookmarks/links keep working. */}
                        <Route
                          path="/admin/reconciliation"
                          element={<Navigate to="/cleanup" replace />}
                        />
                        {/* Impersonation Log is a tab on the Team page now (2026-07-24 nav
              map §3); the old route 302s to that tab. */}
                        <Route
                          path="/admin/impersonation-log"
                          element={<Navigate to="/admin/team?tab=impersonation" replace />}
                        />
                        {/* PR #9 — Ask Stew V1 Extended. Feedback queue with 6
              outcome_kind buckets, 7d/30d trend, top off-schema
              customization asks, triage flow. system_admin OR
              compliance can read; mutations are system_admin-only
              (server-enforced + the page hides the buttons). */}
                        {/* Exam packs (Document Compliance Program) — the logs a
              regulator asks for, one per company. Two of them (DIFI's
              loan log, ADRE's transaction log) are records the rules
              require the firm to keep. system_admin OR compliance;
              the endpoints enforce the same gate. */}
                        <Route
                          path="/admin/exam-packs"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Exam Packs">
                                <AdminExamPacksPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Company Coverage — Stewardship's OWN insurance (step 1,
              2026-10-01; docs/design/company-coverage.md). Admins and company
              owners; the server decides which policies each one sees. The
              literal paths come before /:id so "life" and "new" are never
              read as policy ids. */}
                        <Route
                          path="/company-coverage"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canOpenSharedCompanyPolicy}
                                pageName="Company Coverage"
                              >
                                <CompanyCoveragePage view="business" />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/life"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyCoveragePage view="life" />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/new"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyPolicyFormPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/import"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyCoverageImportPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/import/:importId"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyCoverageImportPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/missing"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyCoverageMissingPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/missing/:reviewId"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyCoverageMissingPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/check"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyCoverageCheckPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/reviews/:reviewId"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canOpenSharedCompanyPolicy}
                                pageName="Company Coverage"
                              >
                                <CompanyRenewalReviewPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/:id"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canOpenSharedCompanyPolicy}
                                pageName="Company Coverage"
                              >
                                <CompanyPolicyPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/company-coverage/:id/edit"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeCompanyCoverage}
                                pageName="Company Coverage"
                              >
                                <CompanyPolicyFormPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Legal holds (Document Compliance Program, R-3) — a hold
              that covers a whole matter rather than one document, so a
              dispute does not depend on somebody enumerating every file.
              system_admin OR compliance; the endpoints enforce the same. */}
                        <Route
                          path="/admin/legal-holds"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Legal Holds">
                                <AdminLegalMattersPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* The retention schedule (Document Compliance Program).
              READ is open to every employee — "why is this kept ten
              years?" is a fair question for anyone filing a document,
              and the citation is what a published schedule is for.
              Changing a period is system_admin OR compliance, enforced
              at the endpoint; the page hides the control otherwise. */}
                        <Route
                          path="/retention-schedule"
                          element={
                            <Authed>
                              <AdminRetentionSchedulePage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/stew-feedback"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Stew Feedback">
                                <AdminStewFeedbackPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Database health (portability program, Lane E) — the
              four readings that decide whether the database is keeping
              up. Absorbed the old /admin/slow-queries page, which now
              redirects here so an old bookmark still lands somewhere. */}
                        <Route
                          path="/admin/database-health"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Database Health"
                              >
                                <AdminDatabaseHealthPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Email Health (2026-10-07) — the DMARC reports from
              dmarc@stewardship.pro, read by a daily job and turned into
              one verdict. Admins read; "Check now" is system_admin only,
              enforced server-side. */}
                        <Route
                          path="/admin/email-health"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Email Health">
                                <AdminEmailHealthPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Staging Outbox (2026-11-02): every email staging sent
              or held. System admins (qa.admin included); the API answers
              404 off staging and the page says so. */}
                        <Route
                          path="/admin/staging-outbox"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={(u) => u.employee_role_slug === "system_admin"}
                                pageName="Staging Outbox"
                              >
                                <AdminStagingOutboxPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/slow-queries"
                          element={<Navigate to="/admin/database-health" replace />}
                        />
                        {/* Plaid Limited Production test (limited-production-plan.md,
              build 3). Admins can open the page; the server decides who
              can act (system admin, as themselves, with a fresh passkey,
              switch on) and says which lock is missing. */}
                        <Route
                          path="/admin/plaid-live-test"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Plaid live test"
                              >
                                <AdminPlaidLiveTestPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Where a bank sends you back after its own sign-in
              (spec 6.4). Registered in Plaid's dashboard; must stay this
              exact path with no query string. */}
                        <Route
                          path="/connect/bank/return"
                          element={
                            <Authed>
                              <PlaidBankReturnPage />
                            </Authed>
                          }
                        />
                        {/* Native scheduling Phase B1 — the /book routing
              registry (needs, rules, pools, live coverage). Reads are
              open to any employee; saving is system_admin-only,
              enforced server-side. */}
                        <Route
                          path="/admin/booking-routing"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Booking Routing"
                              >
                                <AdminBookingRoutingPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* D2 + E3 cutover (2026-05-18) + E2 cutover (2026-05-19):
              the Data Hygiene dashboard + every dedup / structure /
              data-quality / spouse-pass queue AND the pending-
              conflicts queue are replaced by /cleanup (17 detectors,
              verified parity). All old URLs 302-redirect so
              bookmarks/links keep working. The exact
              pending-conflicts path is declared further below so
              React Router ranks it above this splat and it also
              302s to /cleanup. Engine internals (data-hygiene.ts
              detection, source-priority/pending_conflicts substrate,
              merge/rollback/quarantine substrate, the admin API
              routes) are kept — only the user-facing pages are gone. */}
                        <Route
                          path="/admin/data-hygiene"
                          element={<Navigate to="/cleanup" replace />}
                        />
                        <Route
                          path="/admin/data-hygiene/*"
                          element={<Navigate to="/cleanup" replace />}
                        />
                        <Route
                          path="/admin/data-imports/financial"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Financial Data Imports"
                              >
                                <AdminDataImportFinancialPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commission-reconciliation"
                          element={
                            <Authed>
                              {/*
                            Shared between Grant and Brandon — a wider
                            predicate than the rest of the admin section
                            on purpose (Grant, 2026-08-28). See
                            canSeeCommissionReconciliation.
                          */}
                              <RequireRole
                                predicate={canSeeCommissionReconciliation}
                                pageName="Commission Reconciliation"
                              >
                                <AdminCommissionReconciliationPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/insurance-reconciliation"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Insurance Reconciliation"
                              >
                                <AdminInsuranceReconciliationPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/dashboard/insurance"
                          element={
                            <Authed>
                              <InsuranceDashboardPage />
                            </Authed>
                          }
                        />
                        {/* The Planning book of business (S20) — every line of
              business Planning writes, each one clicking through to the
              cases behind it. Scope-gated to Planning (plus the unscoped
              roles); the API enforces the same rule. */}
                        <Route
                          path="/planning/book-of-business"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeePlanningBook}
                                pageName="Book of Business"
                              >
                                <PlanningBookOfBusinessPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Planning deposits (commissions step C6, part 2):
              place each carrier deposit on the cases it pays. Planning's
              people, system admins and compliance; the API enforces the
              same rule. */}
                        <Route
                          path="/planning/deposits"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeePlanningDeposits}
                                pageName="Planning deposits"
                              >
                                <PlanningDepositsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Structured Notes — Stewardship Financial's note
              monitoring board. Scope-gated to Financial (plus the
              unscoped roles); the API enforces the same rule. */}
                        <Route
                          path="/structured-notes"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeStructuredNotes}
                                pageName="Structured Notes"
                              >
                                <StructuredNotesPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/financial/firm-dashboard"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Financial Firm Dashboard"
                              >
                                <AdminFinancialFirmDashboardPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Founder deeper-dive (Phase 4, source-attribution program):
              where business comes from, by volume AND revenue. */}
                        <Route
                          path="/admin/referral-network"
                          element={<Navigate to="/marketing/referral-network" replace />}
                        />
                        <Route
                          path="/admin/financial/fee-review"
                          element={
                            <Authed>
                              {/* Wider than canSeeAdminSection on purpose —
                              running the review is Jake's own compliance
                              duty. Mirrors the server gate. */}
                              <RequireRole
                                predicate={canSeeFeeReview}
                                pageName="Monthly Fee Review"
                              >
                                <AdminFeeReviewPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/financial/deposits"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Financial Commission Deposits"
                              >
                                <AdminFinancialDepositsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* One Compliance page with company tabs absorbs the four
              per-company pages (2026-07-24 nav map §3). Old per-company URLs
              302 to their tab. */}
                        <Route
                          path="/admin/company-compliance"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Compliance">
                                <AdminCompanyCompliancePage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/mortgage-compliance"
                          element={<Navigate to="/admin/company-compliance?tab=mortgage" replace />}
                        />
                        <Route
                          path="/my-mortgage-compliance"
                          element={
                            <Authed>
                              <MyMortgageCompliancePage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/realty-compliance"
                          element={<Navigate to="/admin/company-compliance?tab=realty" replace />}
                        />
                        {/* Folded into Industry Documents (2026-09-02) — kept
                        so Nate's bookmarks and the two in-app links to
                        the old address keep working. */}
                        <Route
                          path="/admin/realty-forms-library"
                          element={<Navigate to="/esign/industry-documents/realty" replace />}
                        />
                        <Route
                          path="/admin/insurance-compliance"
                          element={
                            <Navigate to="/admin/company-compliance?tab=insurance" replace />
                          }
                        />
                        <Route
                          path="/admin/planning-compliance"
                          element={<Navigate to="/admin/company-compliance?tab=planning" replace />}
                        />
                        {/* E2 cutover (2026-05-19): the source-priority-conflict
              detector makes /cleanup a superset of pending-conflicts,
              so the page is deleted and its route 302s to /cleanup
              (this exact path must still be declared so it outranks
              the /admin/data-hygiene/* redirect and lands here). */}
                        <Route
                          path="/admin/data-hygiene/queues/pending-conflicts"
                          element={<Navigate to="/cleanup" replace />}
                        />
                        <Route
                          path="/admin/integrations"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Integrations">
                                <AdminIntegrationsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/integrations/gusto"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Connect Gusto"
                              >
                                <AdminIntegrationsGustoPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/integrations/quickbooks"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="QuickBooks Integration"
                              >
                                <AdminIntegrationsQuickBooksPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/integrations/microsoft-graph"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Office 365 Integration"
                              >
                                <AdminIntegrationsMicrosoftGraphPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Marketing & Ads Phase 0 — dark behind the server-side
              ADS_AUDIENCE_EXPORT_ENABLED flag; page shows a "not
              enabled" note while the API 404s. */}
                        <Route
                          path="/admin/integrations/ad-audiences"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Ad Audiences">
                                <AdminIntegrationsAdAudiencesPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* IVANS match review (2026-08-13, Phase C1.5) — the
              Insurance-facing grading queue. NOT admin-gated: the
              person who knows whether a transaction landed on the right
              family is the insurance servicer, and she needs to run it
              from her own login. Server enforces insurance scope. */}
                        <Route
                          path="/insurance/ivans-review"
                          element={
                            <Authed>
                              <IvansMatchReviewPage />
                            </Authed>
                          }
                        />
                        {/* Carrier activity (2026-09-09) — the log of what the
              carriers sent. Same shape as the review queue above: no
              RequireRole here because the gate is INSURANCE SCOPE, and
              the server enforces it (routes/ivans-activity.ts). It
              exists because every other view of this data is admin-only,
              which 403s the person who services the book. */}
                        <Route
                          path="/insurance/carrier-activity"
                          element={
                            <Authed>
                              <CarrierActivityPage />
                            </Authed>
                          }
                        />
                        {/* IVANS carrier feed (2026-08-12, Phase A). Feed health
              + the Cloud PC uploader's endpoint/token. Read paths are
              admin; the token is system_admin only, server-side. */}
                        <Route
                          path="/admin/integrations/ivans"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="IVANS carrier data"
                              >
                                <AdminIntegrationsIvansPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Microsoft Bookings → OS contact sync (Grant, 2026-07-31):
              replaces the retired Calendly → Zapier → AC chain. Status
              + "Sync now" + per-page off switch. */}
                        <Route
                          path="/admin/integrations/bookings"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Microsoft Bookings"
                              >
                                <AdminIntegrationsBookingsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Compliance Program PR 16 — Medicare call recording
              upload (Ryan's iPhone flow). Any employee may use it;
              the sidebar shortcut is planning-scoped. */}
                        <Route
                          path="/call-recordings/upload"
                          element={
                            <Authed>
                              <CallRecordingUploadPage />
                            </Authed>
                          }
                        />
                        {/* v1.x phase 11.16 (Tier A item 5) — system_admin-only
              one-time SharePoint folder-shape migration. UI replaces
              the curl flow in docs/operations/sharepoint-setup.md. */}
                        <Route
                          path="/admin/sharepoint-migrate"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canRunSharePointMigration}
                                pageName="SharePoint folder migration"
                              >
                                <AdminSharePointMigratePage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* One-time "advisor departed" fit backlog clear (2026-07-01).
              system_admin only — it writes fit history in bulk. */}
                        <Route
                          path="/admin/fit-cleanup"
                          element={
                            <Authed>
                              <RequireRole predicate={canRunFitCleanup} pageName="Fit cleanup">
                                <AdminFitCleanupPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/compliance/rules"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Compliance Rules"
                              >
                                <AdminCompliancePage tab="rules" />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* The old hidden editor (system admin and compliance only)
                        moved to the Checklists area on 2026-09-29, where every
                        company manages its own checklists. */}
                        <Route
                          path="/admin/compliance/checklists"
                          element={<Navigate to="/checklists" replace />}
                        />
                        <Route
                          path="/checklists"
                          element={
                            <Authed>
                              <ChecklistsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/checklists/:id"
                          element={
                            <Authed>
                              <ChecklistEditorPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/compliance/instances"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Checklist Instances"
                              >
                                <AdminCompliancePage tab="instances" />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/compliance/:company"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Compliance Issues"
                              >
                                <AdminCompliancePage tab="issues" />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Comp plans (build step B2): system admins only, the
              same gate as the API (Grant, 2026-10-15). The old splits page
              is read-only history now, under the same gate. */}
                        <Route
                          path="/admin/commission-splits"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeTeamPage}
                                pageName="Commission Splits"
                              >
                                <AdminCommissionSplitsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/team-compensation"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeTeamPage} pageName="Team Compensation">
                                <TeamCompensationPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/team/:userId/comp-plan"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeTeamPage} pageName="Comp plan">
                                <CompPlanPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commissions"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Commissions">
                                <CommissionsListPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Registered BEFORE /admin/commissions/:id so the
                      literal "payroll" segment isn't swallowed as a
                      disbursement id. */}
                        <Route
                          path="/admin/commissions/payroll"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Commission Payroll"
                              >
                                <CommissionPayrollPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commissions/pay-runs"
                          element={
                            <Authed>
                              <PayRunsIndex />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commissions/payroll/:payDate"
                          element={
                            <Authed>
                              <PayDatePage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commissions/payroll/:payDate/:company"
                          element={
                            <Authed>
                              <PayRunPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commissions/john-hancock"
                          element={
                            <Authed>
                              <JohnHancockFilePage />
                            </Authed>
                          }
                        />
                        {/* Step C1: the operations desk. The server refuses
                            anyone not on the desk list, with a sentence. */}
                        <Route
                          path="/desk"
                          element={
                            <Authed>
                              <DeskPage />
                            </Authed>
                          }
                        />
                        {/* Step B5: one person's pay statement, for payroll. The
                            server decides who may read it (anyone who may see
                            the company's runs). */}
                        <Route
                          path="/admin/commissions/statements/:statementId"
                          element={
                            <Authed>
                              <StatementPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commissions/unpaid"
                          element={
                            <Authed>
                              <UnpaidEarningsPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/commissions/:id"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Commission Worksheet"
                              >
                                <CommissionWorksheetPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/compliance/loan-files/:loanId"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Loan File Audit"
                              >
                                <LoanFileAuditPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/my-commissions"
                          element={
                            <Authed>
                              <MyCommissionsPage />
                            </Authed>
                          }
                        />
                        {/* Each person's own comp plan, read-only (build step B2).
              The API returns only the signed-in person's plans. */}
                        <Route
                          path="/my-comp-plan"
                          element={
                            <Authed>
                              <MyCompPlanPage />
                            </Authed>
                          }
                        />
                        {/* Step B5: the person's own pay statement (live runs
                            only; the API refuses a shadow one). */}
                        <Route
                          path="/my-comp-plan/statements/:statementId"
                          element={
                            <Authed>
                              <StatementPage />
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/settings"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Settings">
                                <AdminSettingsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/at-risk-calibration"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="At-Risk Calibration"
                              >
                                <AtRiskCalibrationPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/watchlist-rules"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Watchlist Rules"
                              >
                                <WatchlistRulesPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* One tabbed Dashboards & Targets page absorbs the three former
              config pages (2026-07-24 nav map §3). */}
                        <Route
                          path="/admin/dashboards-targets"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canSeeAdminSection}
                                pageName="Dashboards & Targets"
                              >
                                <AdminDashboardsTargetsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Old rail links 302 to their tab. */}
                        <Route
                          path="/admin/dashboard-events"
                          element={<Navigate to="/admin/dashboards-targets?tab=pins" replace />}
                        />
                        <Route
                          path="/admin/dashboard-targets"
                          element={<Navigate to="/admin/dashboards-targets?tab=targets" replace />}
                        />
                        <Route
                          path="/admin/metric-contributors"
                          element={
                            <Navigate to="/admin/dashboards-targets?tab=contributors" replace />
                          }
                        />
                        <Route
                          path="/admin/view-defaults"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="View defaults">
                                <AdminViewDefaultsPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        {/* Firm profiles (2026-10-07): every company's firm block on
                        one page. The admin-section gate opens the page; which
                        blocks a person sees, and which they may save, is the
                        firm-profile route's own company-scope and role test. */}
                        <Route
                          path="/admin/firm-profiles"
                          element={
                            <Authed>
                              <RequireRole predicate={canSeeAdminSection} pageName="Firm profiles">
                                <AdminFirmProfilesPage />
                              </RequireRole>
                            </Authed>
                          }
                        />
                        <Route
                          path="/admin/shared-mailboxes"
                          element={
                            <Authed>
                              <RequireRole
                                predicate={canManageSharedMailboxAccess}
                                pageName="Shared Mailbox Access"
                              >
                                <AdminSharedMailboxAccessPage />
                              </RequireRole>
                            </Authed>
                          }
                        />

                        {/* Personal settings — ONE tabbed page (2026-07-24 nav map §4).
              Security & 2FA stays on its own /security route (MFA gate). */}
                        <Route
                          path="/settings"
                          element={
                            <Authed>
                              <SettingsPage />
                            </Authed>
                          }
                        />
                        {/* The old scattered settings routes 302 to the matching tab so
              bookmarks keep working. */}
                        <Route
                          path="/profile"
                          element={<Navigate to="/settings?tab=profile" replace />}
                        />
                        <Route
                          path="/preferences"
                          element={<Navigate to="/settings?tab=preferences" replace />}
                        />
                        <Route
                          path="/preferences/ai"
                          element={<Navigate to="/settings?tab=ai" replace />}
                        />
                        <Route
                          path="/notifications"
                          element={
                            <Authed>
                              <NotificationsPage />
                            </Authed>
                          }
                        />

                        {/* Fallback — unknown path. Redirect home WITH a notice so
              a typo'd URL doesn't silently teleport the user to their
              dashboard with no explanation (2026-06-09 audit, 8.1).
              NoticeFromQuery in PageLayout turns the param into a
              toast on arrival. */}
                        <Route path="*" element={<UnknownRouteRedirect />} />
                      </Routes>
                    </Suspense>
                    {/* Phase D6 universal Undo toast. Polls /api/recent-actions
            every 30s; surfaces the newest destructive action with a
            one-click rollback. Hidden when nothing's in the buffer. */}
                    <UndoToast />
                    {/* Marketing Hub tables read as cards on a phone (audit 2026-10-27). */}
                    <HubTableCards />
                  </CleanupProvider>
                </EmailComposerProvider>
              </UserSettingsProvider>
            </ToastProvider>
          </NavHistoryProvider>
        </BrowserRouter>
      </MfaProvider>
    </SessionProvider>
  );
}
